import React, { useState, useCallback, useRef, useEffect } from 'react';
import { PanelLeft, Menu, Undo2, Redo2, RefreshCw, Printer } from 'lucide-react';
import { cn } from '@/app/components/ui/utils';
import { Button } from './ui/button';
import { NavigationDrawer } from './NavigationDrawer';
import { TutorialHelpButton } from '@/app/components/tutorial/TutorialHelpButton';
import { CoordinateSystemWidget } from './CoordinateSystemWidget';
import type { SystemData, PlacedBlock, SocketConnection, IsomProject } from '@/app/types/cad';
import type { 
  TableConfig, 
  TableCell, 
  TableSelection, 
  TableHistory, 
  HeaderGroup, 
  HeaderRow, 
  HeaderCell, 
  HeaderCellSelection,
  BorderStyle,
  GroupConfig,
  AggregationType
} from '@/app/types/table';
import { TableGrid } from './table/TableGrid';
import { TableSidebar } from './table/TableSidebar';
import { TableMainToolbar } from './table/TableMainToolbar';
import { BorderAreasDialog, type BorderAreaConfig } from './table/BorderAreasDialog';
import { AddAttributeDialog, AddAttributeConfig } from './table/AddAttributeDialog';
import { MergeColumnsDialog } from './table/MergeColumnsDialog';
import { RenameColumnDialog } from './table/RenameColumnDialog';
import { MergedColumnDialog } from './table/MergedColumnDialog';
import { PrintDialog } from './print/PrintDialog';
import { GroupByPanel } from './table/GroupByPanel';
import { createEmptyTableConfig, addAttributeColumn } from '@/app/utils/tableUtils';
import { addAttributeToObjects } from '@/app/utils/tableUtils';
import { updateAllLengthAttributes } from '@/app/utils/lengthCalculation';
import { savePrintConfig, saveCustomPresets } from '@/app/utils/printPersistence';
import { buildGroupedRows, toggleGroupCollapse, createDefaultGroupConfig, createDefaultTotalsRowConfig } from '@/app/utils/groupingUtils';
import { toast } from 'sonner';
import { useLanguage } from '@/app/contexts/LanguageContext';

interface SystemTableEditorProps {
  systems: SystemData[];
  onUpdateSystem: (systemId: string, updates: Partial<SystemData>) => void;
  projectData: {
    name: string;
    customAttributes: { [key: string]: string | number };
  };
  onUpdateProjectData: (updates: any) => void;
  blocks?: PlacedBlock[];
  connections?: SocketConnection[];
  onBlocksChange?: (blocks: PlacedBlock[]) => void;
  onConnectionsChange?: (connections: SocketConnection[]) => void;
}

export function SystemTableEditor({
  systems,
  onUpdateSystem,
  projectData,
  onUpdateProjectData,
  blocks = [],
  connections = [],
  onBlocksChange,
  onConnectionsChange,
}: SystemTableEditorProps) {
  const { t } = useLanguage();
  const [tableConfig, setTableConfig] = useState<TableConfig>(() => {
    try {
      const saved = localStorage.getItem('cad_tableConfig');
      if (saved) {
        const parsed = JSON.parse(saved);
        
        // MIGRATION: Convert old headerGroups format to new format
      if (parsed.headerGroups && parsed.headerGroups.length > 0) {
        parsed.headerGroups = parsed.headerGroups.map((group: any) => {
          // Check if group uses old format (startColumnIndex/endColumnIndex)
          if (group.startColumnIndex !== undefined && !group.columnIds) {
            console.log('🔄 Migrating headerGroup from old format:', group);
            
            // Convert indices to column IDs
            const columnIds: string[] = [];
            for (let i = group.startColumnIndex; i <= group.endColumnIndex; i++) {
              const col = parsed.columns[i];
              if (col) {
                columnIds.push(col.id);
              }
            }
            
            // Return new format
            return {
              id: group.id,
              label: group.label,
              columnIds,
              level: group.level,
            };
          }
          
          // Already in new format
          return group;
        });
        
        console.log('✅ HeaderGroups migrated successfully');
      }
      
      // MIGRATION: Add missing cell IDs
      if (parsed.rows && Array.isArray(parsed.rows)) {
        console.log('🔄 Checking for cells without IDs...');
        let migratedCells = 0;
        
        parsed.rows.forEach((row: any, rowIdx: number) => {
          if (row.data && typeof row.data === 'object') {
            Object.keys(row.data).forEach((colId, colIdx) => {
              const cell = row.data[colId];
              if (cell && !cell.id) {
                // Add missing ID
                cell.id = `cell-${row.id}-${colId}`;
                if (cell.rowIndex === undefined) cell.rowIndex = rowIdx;
                if (cell.colIndex === undefined) cell.colIndex = colIdx;
                if (cell.dataType === undefined) cell.dataType = 'text';
                migratedCells++;
              }
            });
          }
        });
        
        if (migratedCells > 0) {
          console.log(`✅ Migrated ${migratedCells} cells with missing IDs`);
        }
      }
      
      // MIGRATION: Fix broken columns (remove headerColSpan=0 and other merge artifacts)
      if (parsed.columns && Array.isArray(parsed.columns)) {
        console.log('🔄 Checking for broken column properties...');
        let fixedColumns = 0;
        
        parsed.columns = parsed.columns.map((col: any, idx: number) => {
          let wasFixed = false;
          
          // Remove any merge-related properties that break rendering
          if ('headerColSpan' in col && col.headerColSpan === 0) {
            console.warn(`⚠️ Fixing column ${col.id} with headerColSpan=0`);
            delete col.headerColSpan;
            wasFixed = true;
          }
          if ('skipHeaderRows' in col) {
            delete col.skipHeaderRows;
            wasFixed = true;
          }
          if ('headerRowSpan' in col) {
            delete col.headerRowSpan;
            wasFixed = true;
          }
          if ('coveredByRowspan' in col) {
            delete col.coveredByRowspan;
            wasFixed = true;
          }
          
          // Ensure column is visible
          if (col.isVisible === false) {
            console.warn(`⚠️ Fixing hidden column ${col.id}`);
            col.isVisible = true;
            wasFixed = true;
          }
          
          // Ensure column has correct index
          if (col.index !== idx) {
            col.index = idx;
            wasFixed = true;
          }
          
          // Ensure column has a label (use attributeKey as fallback)
          if (!col.label && col.attributeKey) {
            console.warn(`⚠️ Column ${col.id} missing label, using attributeKey: ${col.attributeKey}`);
            col.label = col.attributeKey;
            wasFixed = true;
          } else if (!col.label) {
            console.warn(`⚠️ Column ${col.id} missing label and attributeKey!`);
            col.label = '(Unbenannt)';
            wasFixed = true;
          }
          
          // Ensure column has minimum width
          if (!col.width || col.width < 50) {
            console.warn(`⚠️ Column ${col.id} has invalid width (${col.width}), setting to 150px`);
            col.width = 150;
            wasFixed = true;
          }
          
          if (wasFixed) fixedColumns++;
          return col;
        });
        
        if (fixedColumns > 0) {
          console.log(`✅ Fixed ${fixedColumns} broken columns`);
        }
      }
      
      // Initialize groupConfig if missing
      if (!parsed.groupConfig) {
        parsed.groupConfig = createDefaultGroupConfig();
      } else {
        // Deserialize collapsedGroups (Array -> Set)
        if (Array.isArray(parsed.groupConfig.collapsedGroups)) {
          parsed.groupConfig.collapsedGroups = new Set(parsed.groupConfig.collapsedGroups);
        } else if (!parsed.groupConfig.collapsedGroups) {
          parsed.groupConfig.collapsedGroups = new Set();
        }
      }
      
      // Initialize totalsRowConfig if missing
      if (!parsed.totalsRowConfig) {
        parsed.totalsRowConfig = createDefaultTotalsRowConfig();
      }
      
      return parsed;
      }
    } catch (e) {
      console.error('Failed to load table config from localStorage:', e);
    }
    
    const emptyConfig = createEmptyTableConfig();
    emptyConfig.groupConfig = createDefaultGroupConfig();
    emptyConfig.totalsRowConfig = createDefaultTotalsRowConfig();
    return emptyConfig;
  });
  
  // 🔥 CRITICAL: Sync table with current blocks/connections WHENEVER they change!
  // REPLACED incremental sync with DESTRUCTIVE REBUILD logic to ensure consistency on paste/delete
  useEffect(() => {
    // Skip if no table columns exist yet
    if (tableConfig.columns.length === 0) {
      return;
    }
    
    // Perform full rebuild of data rows
    setTableConfig(prevConfig => {
      console.log('🔄 Auto-Rebuild: Syncing table with blocks/connections...');
      
      // 1. Calculate restored blocks (with correct lengths) locally
      // We do NOT update parent state here to avoid infinite loops
      const restoredBlocks = updateAllLengthAttributes(blocks);
      
      const config = { ...prevConfig };
      const headerRows = config.rows.filter(r => r.isHeader);
      const oldRows = config.rows.filter(r => !r.isHeader);
      
      // 2. Generate rows for ALL blocks
      const blockRows = restoredBlocks.map((block, idx) => {
        // Try to preserve row ID if it existed before (to keep selection state if possible)
        const existingRow = oldRows.find(r => r.blockId === block.id);
        const rowId = existingRow ? existingRow.id : `row-${Date.now()}-${Math.random().toString(36).substring(2, 9)}_${idx}`;
        
        const allAttrs: any = { 'Name': block.blockData.name || 'Unnamed' };
        block.blockData.attributes?.forEach((attr: any) => {
          allAttrs[attr.name] = attr.value;
        });
        
        const cells: { [columnId: string]: TableCell } = {};
        config.columns.forEach((col: any, colIdx: number) => {
          let attributeRef = '';
          if (col.type === 'name') {
            attributeRef = 'Name';
          } else if (col.type === 'attribute' && col.sourceAttributeId) {
            attributeRef = col.sourceAttributeId;
          } else if (col.attributeKey) {
            attributeRef = col.attributeKey;
          }
          
          const value = attributeRef && allAttrs[attributeRef] !== undefined ? allAttrs[attributeRef] : '';
          
          cells[col.id] = {
            id: `cell-${rowId}-${col.id}`,
            rowIndex: 0, // Will be set later
            colIndex: colIdx,
            value,
            dataType: col.dataType || 'text',
            style: {},
            attributeRef,
          };
        });
        
        return {
          id: rowId,
          index: 0,
          height: 30,
          isHeader: false,
          isEmpty: false,
          blockId: block.id,
          locked: false,
          data: cells,
          allAttributes: allAttrs,
        };
      });
      
      // 3. Generate rows for ALL connections
      const connectionRows = connections.map((connection, idx) => {
        const existingRow = oldRows.find(r => r.connectionId === connection.id);
        const rowId = existingRow ? existingRow.id : `row-${Date.now()}-${Math.random().toString(36).substring(2, 9)}_c${idx}`;
        
        const allAttrs: any = { 'Name': 'Verbindung' };
        connection.attributes?.forEach((attr: any) => {
          allAttrs[attr.name] = attr.value;
        });
        
        const cells: { [columnId: string]: TableCell } = {};
        config.columns.forEach((col: any, colIdx: number) => {
          let attributeRef = '';
          if (col.type === 'name') {
            attributeRef = 'Name';
          } else if (col.type === 'attribute' && col.sourceAttributeId) {
            attributeRef = col.sourceAttributeId;
          } else if (col.attributeKey) {
            attributeRef = col.attributeKey;
          }
          
          const value = attributeRef && allAttrs[attributeRef] !== undefined ? allAttrs[attributeRef] : '';
          
          cells[col.id] = {
            id: `cell-${rowId}-${col.id}`,
            rowIndex: 0,
            colIndex: colIdx,
            value,
            dataType: col.dataType || 'text',
            style: {},
            attributeRef,
          };
        });
        
        return {
          id: rowId,
          index: 0,
          height: 30,
          isHeader: false,
          isEmpty: false,
          connectionId: connection.id,
          locked: false,
          data: cells,
          allAttributes: allAttrs,
        };
      });
      
      // 4. Combine and Re-Index
      const newRows = [...headerRows, ...blockRows, ...connectionRows];
      newRows.forEach((row, idx) => {
        row.index = idx;
        Object.values(row.data).forEach((cell: any) => {
          cell.rowIndex = idx;
        });
      });
      
      // Check if actually changed (simplified check: count)
      // Note: We always return new config to ensure data values are fresh
      return { ...config, rows: newRows };
    });
  }, [blocks, connections, tableConfig.columns.length]); // Re-run whenever blocks/connections change

  // 🧪 TEST: Monitor tableConfig changes
  useEffect(() => {
    console.log('🧪 TABLE-STATE-CHECK:', {
      blocksCount: blocks.length,
      connectionsCount: connections.length,
      columnsCount: tableConfig.columns.length,
      rowsCount: tableConfig.rows.length,
      dataRows: tableConfig.rows.filter(r => !r.isHeader).length
    });
  }, [tableConfig.rows.length]); // Run whenever rows count changes

  const [selection, setSelection] = useState<TableSelection | null>(null);
  const [history, setHistory] = useState<TableHistory>({ past: [], future: [] });
  const [editingCell, setEditingCell] = useState<{ rowId: string; colId: string } | null>(null);
  
  // Header cell editing: { rowId, cellId }
  const [editingHeaderCell, setEditingHeaderCell] = useState<{ rowId: string; cellId: string } | null>(null);
  
  // Header cell selection: { rowId, cellId }[]
  const [headerCellSelection, setHeaderCellSelection] = useState<Array<{ rowId: string; cellId: string }>>([]);
  
  // Border dialog
  const [showBorderDialog, setShowBorderDialog] = useState(false);
  
  // Add Column dialog state
  const [showAddColumnDialog, setShowAddColumnDialog] = useState(false);
  
  // Print dialog state
  const [showPrintDialog, setShowPrintDialog] = useState(false);
  
  // Grouping panel state
  const [showGroupByPanel, setShowGroupByPanel] = useState(false);
  
  // Responsive Sidebar State
  const [showSidebar, setShowSidebar] = useState(true);
  
  // Navigation Drawer State
  const [drawerOpen, setDrawerOpen] = useState(false);
  
  // Legacy header selection (main header columns) - keeping for compatibility
  const [headerSelection, setHeaderSelection] = useState<number[] | null>(null);
  const [showRenameDialog, setShowRenameDialog] = useState(false);
  const [showMergeDialog, setShowMergeDialog] = useState(false);
  // showAddHeaderDialog removed - header features disabled
  
  const containerRef = useRef<HTMLDivElement>(null);

  // Undo/Redo functionality
  const pushToHistory = useCallback((newConfig: TableConfig) => {
    setHistory(prev => ({
      past: [...prev.past, tableConfig],
      future: [],
    }));
    setTableConfig(newConfig);
  }, [tableConfig]);

  const handleUndo = useCallback(() => {
    if (history.past.length === 0) return;
    
    const previous = history.past[history.past.length - 1];
    const newPast = history.past.slice(0, -1);
    
    setHistory({
      past: newPast,
      future: [tableConfig, ...history.future],
    });
    setTableConfig(previous);
    toast.success('Rückgängig');
  }, [history, tableConfig]);

  const handleRedo = useCallback(() => {
    if (history.future.length === 0) return;
    
    const next = history.future[0];
    const newFuture = history.future.slice(1);
    
    setHistory({
      past: [...history.past, tableConfig],
      future: newFuture,
    });
    setTableConfig(next);
    toast.success('Wiederholen');
  }, [history, tableConfig]);

  // Cell value update
  const handleCellValueChange = useCallback((
    rowId: string,
    colId: string,
    newValue: string | number | boolean
  ) => {
    const newConfig = { ...tableConfig };
    const rowIndex = newConfig.rows.findIndex(r => r.id === rowId);
    
    if (rowIndex !== -1) {
      const cell = newConfig.rows[rowIndex].data[colId];
      if (cell) {
        newConfig.rows[rowIndex].data[colId] = {
          ...cell,
          value: newValue,
        };
      }
    }

    pushToHistory(newConfig);
  }, [tableConfig, pushToHistory]);
  
  // ===== GROUPING HANDLERS =====
  const handleUpdateGroupConfig = useCallback((newGroupConfig: GroupConfig) => {
    const newConfig = {
      ...tableConfig,
      groupConfig: newGroupConfig,
    };
    pushToHistory(newConfig);
    toast.success(newGroupConfig.enabled ? t('table.grouping.activated') : t('table.grouping.deactivated'));
  }, [tableConfig, pushToHistory, t]);
  
  const handleToggleGroupCollapse = useCallback((groupKey: string) => {
    if (!tableConfig.groupConfig) return;
    
    const newGroupConfig = toggleGroupCollapse(tableConfig.groupConfig, groupKey);
    const newConfig = {
      ...tableConfig,
      groupConfig: newGroupConfig,
    };
    setTableConfig(newConfig); // No history push for collapse/expand
  }, [tableConfig]);
  
  // ===== TOTALS ROW HANDLERS =====
  const handleToggleTotalsRow = useCallback(() => {
    if (!tableConfig.totalsRowConfig) return;
    
    const newConfig = {
      ...tableConfig,
      totalsRowConfig: {
        ...tableConfig.totalsRowConfig,
        enabled: !tableConfig.totalsRowConfig.enabled,
      },
    };
    pushToHistory(newConfig);
    toast.success(newConfig.totalsRowConfig.enabled ? t('table.totals.activated') : t('table.totals.deactivated'));
  }, [tableConfig, pushToHistory, t]);
  
  const handleUpdateTotalsAggregation = useCallback((columnId: string, aggregation: AggregationType) => {
    if (!tableConfig.totalsRowConfig) return;
    
    const newConfig = {
      ...tableConfig,
      totalsRowConfig: {
        ...tableConfig.totalsRowConfig,
        aggregations: {
          ...tableConfig.totalsRowConfig.aggregations,
          [columnId]: aggregation,
        },
      },
    };
    pushToHistory(newConfig);
  }, [tableConfig, pushToHistory]);

  // Add attribute column via drag & drop
  const handleAttributeDrop = useCallback((attributeName: string, columnIndex?: number) => {
    const newConfig = addAttributeColumn(tableConfig, attributeName, blocks, connections);
    pushToHistory(newConfig);
  }, [tableConfig, blocks, connections, pushToHistory]);

  // Create new attribute and add to objects
  const handleCreateAttribute = useCallback((config: AddAttributeConfig) => {
    const result = addAttributeToObjects(
      tableConfig,
      config,
      blocks,
      connections
    );

    // Update blocks and connections in parent
    if (onBlocksChange) {
      onBlocksChange(result.updatedBlocks);
    }
    if (onConnectionsChange) {
      onConnectionsChange(result.updatedConnections);
    }

    // Add the new attribute as a column
    const newConfig = addAttributeColumn(tableConfig, config.name, result.updatedBlocks, result.updatedConnections);
    pushToHistory(newConfig);
  }, [tableConfig, blocks, connections, onBlocksChange, onConnectionsChange, pushToHistory]);
  
  // Handle "Add Column" button click - opens dialog to create new attribute
  const handleAddColumn = useCallback(() => {
    setShowAddColumnDialog(true);
  }, []);

  // Edit existing attribute
  const handleEditAttribute = useCallback((oldName: string, newConfig: AddAttributeConfig) => {
    // Rename attribute in blocks and connections
    const updatedBlocks = blocks.map(block => {
      if (block.customAttributes && oldName in block.customAttributes) {
        const { [oldName]: value, ...rest } = block.customAttributes;
        return {
          ...block,
          customAttributes: {
            ...rest,
            [newConfig.name]: value,
          },
        };
      }
      return block;
    });

    const updatedConnections = connections.map(conn => {
      if (conn.customAttributes && oldName in conn.customAttributes) {
        const { [oldName]: value, ...rest } = conn.customAttributes;
        return {
          ...conn,
          customAttributes: {
            ...rest,
            [newConfig.name]: value,
          },
        };
      }
      return conn;
    });

    // Update blocks and connections in parent
    if (onBlocksChange) {
      onBlocksChange(updatedBlocks);
    }
    if (onConnectionsChange) {
      onConnectionsChange(updatedConnections);
    }

    // Update column in table config
    const newTableConfig = { ...tableConfig };
    newTableConfig.columns = newTableConfig.columns.map(col => 
      col.attributeKey === oldName 
        ? { ...col, label: newConfig.name, attributeKey: newConfig.name }
        : col
    );

    // Update cells in all rows
    newTableConfig.rows = newTableConfig.rows.map(row => {
      const oldCellId = `${row.id}-${oldName}`;
      const newCellId = `${row.id}-${newConfig.name}`;
      
      const newData: typeof row.data = {};
      Object.entries(row.data).forEach(([key, cell]) => {
        if (cell.id === oldCellId) {
          newData[newConfig.name] = { ...cell, id: newCellId };
        } else {
          newData[key] = cell;
        }
      });

      return { ...row, data: newData };
    });

    pushToHistory(newTableConfig);
  }, [tableConfig, blocks, connections, onBlocksChange, onConnectionsChange, pushToHistory]);

  // Delete attribute
  const handleDeleteAttribute = useCallback((attributeName: string) => {
    console.log('🗑️ handleDeleteAttribute called:', attributeName);
    console.log('📦 Before delete - blocks:', blocks.length, 'connections:', connections.length);
    
    // Remove attribute from blocks and connections
    const updatedBlocks = blocks.map(block => {
      if (block.blockData.attributes && block.blockData.attributes.some(a => a.name === attributeName)) {
        console.log('🔧 Removing from block:', block.id);
        return {
          ...block,
          blockData: {
            ...block.blockData,
            attributes: block.blockData.attributes.filter(a => a.name !== attributeName),
          },
        };
      }
      return block;
    });

    const updatedConnections = connections.map(conn => {
      if (conn.attributes && conn.attributes.some(a => a.name === attributeName)) {
        console.log('🔧 Removing from connection:', conn.id);
        return {
          ...conn,
          attributes: conn.attributes.filter(a => a.name !== attributeName),
        };
      }
      return conn;
    });

    console.log('📦 After delete - updatedBlocks:', updatedBlocks.length, 'updatedConnections:', updatedConnections.length);

    // Update blocks and connections in parent
    if (onBlocksChange) {
      console.log('✅ Calling onBlocksChange');
      onBlocksChange(updatedBlocks);
    } else {
      console.log('❌ No onBlocksChange handler!');
    }
    
    if (onConnectionsChange) {
      console.log('✅ Calling onConnectionsChange');
      onConnectionsChange(updatedConnections);
    } else {
      console.log('❌ No onConnectionsChange handler!');
    }

    // Remove column from table config
    const newTableConfig = { ...tableConfig };
    newTableConfig.columns = newTableConfig.columns.filter(col => col.attributeKey !== attributeName);
    
    // Remove cells from all rows
    newTableConfig.rows = newTableConfig.rows.map(row => ({
      ...row,
      data: Object.fromEntries(
        Object.entries(row.data).filter(([key]) => key !== attributeName)
      ),
    }));

    console.log('✅ Updated table config');
    pushToHistory(newTableConfig);
  }, [tableConfig, blocks, connections, onBlocksChange, onConnectionsChange, pushToHistory]);

  // Save tableConfig to localStorage whenever it changes
  useEffect(() => {
    // Serialize groupConfig properly (Set -> Array)
    const serializable = {
      ...tableConfig,
      groupConfig: tableConfig.groupConfig ? {
        ...tableConfig.groupConfig,
        collapsedGroups: Array.from(tableConfig.groupConfig.collapsedGroups),
      } : undefined,
    };
    localStorage.setItem('cad_tableConfig', JSON.stringify(serializable));
  }, [tableConfig]);

  // Delete row
  const handleDeleteRow = useCallback((rowIndex: number) => {
    const newConfig = { ...tableConfig };
    newConfig.rows.splice(rowIndex, 1);
    
    // Update indices
    for (let i = rowIndex; i < newConfig.rows.length; i++) {
      newConfig.rows[i].index = i;
    }

    pushToHistory(newConfig);
  }, [tableConfig, pushToHistory]);

  // Delete column
  const handleDeleteColumn = useCallback((columnId: string) => {
    const newConfig = { ...tableConfig };
    
    // Find the column index being deleted
    const deletedColIndex = newConfig.columns.findIndex(col => col.id === columnId);
    console.log('🗑️ Deleting column:', { columnId, deletedColIndex });
    
    // Remove column
    newConfig.columns = newConfig.columns.filter(col => col.id !== columnId);
    
    // Remove cells from all data rows
    newConfig.rows = newConfig.rows.map(row => ({
      ...row,
      data: Object.fromEntries(
        Object.entries(row.data).filter(([key]) => key !== columnId)
      ),
    }));
    
    // CRITICAL: Adjust HeaderRows when a column is deleted!
    if (newConfig.headerRows && newConfig.headerRows.length > 0) {
      console.log('📊 Adjusting HeaderRows after column delete...');
      
      newConfig.headerRows = newConfig.headerRows.map(headerRow => {
        const newCells: any[] = [];
        let currentColIdx = 0;
        
        headerRow.cells.forEach(cell => {
          const cellStart = currentColIdx;
          const cellEnd = currentColIdx + (cell.colspan || 1) - 1;
          
          // Check if this cell covers the deleted column
          if (cellStart <= deletedColIndex && deletedColIndex <= cellEnd) {
            // This cell covers the deleted column
            if (cell.colspan && cell.colspan > 1) {
              // Reduce colspan by 1
              console.log(`  📉 Reducing colspan for cell "${cell.label}" from ${cell.colspan} to ${cell.colspan - 1}`);
              newCells.push({
                ...cell,
                colspan: cell.colspan - 1,
              });
            } else {
              // Cell only covers the deleted column - remove it
              console.log(`  🗑️ Removing cell "${cell.label}" (only covered deleted column)`);
            }
          } else if (cellStart > deletedColIndex) {
            // This cell is after the deleted column - keep it
            newCells.push(cell);
          } else {
            // This cell is before the deleted column - keep it
            newCells.push(cell);
          }
          
          currentColIdx += (cell.colspan || 1);
        });
        
        return {
          ...headerRow,
          cells: newCells,
        };
      });
      
      console.log('✅ HeaderRows adjusted');
    }

    pushToHistory(newConfig);
  }, [tableConfig, pushToHistory]);

  // ===== HEADER ROW MANAGEMENT =====
  
  // Add new empty header row
  const handleAddHeaderRow = useCallback(() => {
    const newConfig = { ...tableConfig };
    
    if (!newConfig.headerRows) {
      newConfig.headerRows = [];
    }
    
    // Create a new header row with one cell per column (matching main header structure)
    const cells = newConfig.columns.map((col, idx) => ({
      id: `header-cell-${Date.now()}-${idx}`,
      label: '', // Empty by default
      colspan: 1,
      columnStartIndex: idx,
    }));
    
    const newHeaderRow = {
      id: `header-row-${Date.now()}`,
      cells,
    };
    
    // Insert at position 0 (top, right below the add button)
    newConfig.headerRows.unshift(newHeaderRow);
    
    pushToHistory(newConfig);
    toast.success(t('table.headers.added'));
  }, [tableConfig, pushToHistory, t]);
  
  // Insert new empty header row above a specific header row
  const handleInsertHeaderRowAbove = useCallback((headerRowId: string) => {
    const newConfig = { ...tableConfig };
    
    if (!newConfig.headerRows) {
      newConfig.headerRows = [];
    }
    
    const rowIndex = newConfig.headerRows.findIndex(row => row.id === headerRowId);
    
    if (rowIndex === -1) {
      console.warn('⚠️ HeaderRow not found:', headerRowId);
      return;
    }
    
    // Create a new header row with one cell spanning all columns
    const newHeaderRow = {
      id: `header-row-${Date.now()}`,
      cells: [{
        id: `header-cell-${Date.now()}`,
        label: '', // Empty by default
        colspan: newConfig.columns.length,
        columnStartIndex: 0,
      }],
    };
    
    // Insert at the found index (above the clicked row)
    newConfig.headerRows.splice(rowIndex, 0, newHeaderRow);
    
    console.log('⬆️ Inserted new header row above:', headerRowId, newHeaderRow);
    pushToHistory(newConfig);
    toast.success(t('table.headers.inserted'));
  }, [tableConfig, pushToHistory, t]);
  
  // Delete specific header row
  const handleDeleteHeaderRow = useCallback((headerRowId: string) => {
    const newConfig = { ...tableConfig };
    
    if (!newConfig.headerRows) {
      console.warn('⚠️ No headerRows to delete');
      return;
    }
    
    const rowIndex = newConfig.headerRows.findIndex(row => row.id === headerRowId);
    
    if (rowIndex === -1) {
      console.warn('⚠️ HeaderRow not found:', headerRowId);
      return;
    }
    
    newConfig.headerRows.splice(rowIndex, 1);
    
    console.log('🗑️ Deleted header row:', headerRowId);
    pushToHistory(newConfig);
    toast.success(t('table.headers.deleted'));
  }, [tableConfig, pushToHistory, t]);
  
  // Reset all header rows
  const handleResetHeaderRows = useCallback(() => {
    const newConfig = { ...tableConfig };
    newConfig.headerRows = [];
    
    console.log('🔄 Reset all header rows');
    pushToHistory(newConfig);
    toast.success(t('table.headers.reset'));
  }, [tableConfig, pushToHistory, t]);
  
  // ===== BORDER OPERATIONS =====
  
  // Apply border preset to selected cells
  const handleApplyBorderPreset = useCallback((preset: string) => {
    if (headerCellSelection.length === 0) {
      toast.error(t('table.cells.noSelection'));
      return;
    }
    
    console.log('🎨 Applying border preset:', preset, 'to', headerCellSelection.length, 'cells');
    
    const newConfig = { ...tableConfig };
    
    // Helper to create border definition
    const createBorder = (visible: boolean, width: number = 2, color: string = '#000000', style: BorderStyle = 'solid') => ({
      visible,
      width,
      color,
      style: style as BorderStyle,
    });
    
    // Handle line width presets
    if (preset.startsWith('width-')) {
      const width = parseInt(preset.replace('width-', ''));
      console.log(`📏 Applying line width ${width}px to selected cells`);
      
      for (const sel of headerCellSelection) {
        if (sel.rowId === '__main_columns__') {
          // Main column headers
          const column = newConfig.columns.find(c => c.id === sel.cellId);
          if (column) {
            if (!column.style) column.style = {};
            if (!column.style.borders) column.style.borders = {};
            
            // Apply width to all existing borders
            if (column.style.borders.top) column.style.borders.top.width = width;
            if (column.style.borders.right) column.style.borders.right.width = width;
            if (column.style.borders.bottom) column.style.borders.bottom.width = width;
            if (column.style.borders.left) column.style.borders.left.width = width;
          }
        } else {
          // Header row cells
          const row = newConfig.headerRows?.find(r => r.id === sel.rowId);
          if (row) {
            const cell = row.cells.find(c => c.id === sel.cellId);
            if (cell) {
              if (!cell.style) cell.style = {};
              if (!cell.style.borders) cell.style.borders = {};
              
              // Apply width to all existing borders
              if (cell.style.borders.top) cell.style.borders.top.width = width;
              if (cell.style.borders.right) cell.style.borders.right.width = width;
              if (cell.style.borders.bottom) cell.style.borders.bottom.width = width;
              if (cell.style.borders.left) cell.style.borders.left.width = width;
            }
          }
        }
      }
      
      pushToHistory(newConfig);
      toast.success(t('table.borders.widthApplied').replace('{width}', width.toString()));
      return;
    }
    
    // Handle border style presets
    for (const sel of headerCellSelection) {
      let cell: any = null;
      
      if (sel.rowId === '__main_columns__') {
        // Main column headers
        cell = newConfig.columns.find(c => c.id === sel.cellId);
      } else {
        // Header row cells
        const row = newConfig.headerRows?.find(r => r.id === sel.rowId);
        if (row) {
          cell = row.cells.find(c => c.id === sel.cellId);
        }
      }
      
      if (!cell) continue;
      
      // Initialize style if needed
      if (!cell.style) cell.style = {};
      if (!cell.style.borders) cell.style.borders = {};
      
      // Apply preset
      switch (preset) {
        case 'outer-thick':
          // Thick borders on all sides
          cell.style.borders = {
            top: createBorder(true, 3),
            right: createBorder(true, 3),
            bottom: createBorder(true, 3),
            left: createBorder(true, 3),
          };
          break;
          
        case 'all-borders':
          // Standard borders on all sides
          cell.style.borders = {
            top: createBorder(true, 2),
            right: createBorder(true, 2),
            bottom: createBorder(true, 2),
            left: createBorder(true, 2),
          };
          break;
          
        case 'no-borders':
          // Remove all borders
          cell.style.borders = {
            top: createBorder(false, 0),
            right: createBorder(false, 0),
            bottom: createBorder(false, 0),
            left: createBorder(false, 0),
          };
          break;
          
        case 'horizontal-only':
          // Only top and bottom borders
          cell.style.borders = {
            top: createBorder(true, 2),
            right: createBorder(false, 0),
            bottom: createBorder(true, 2),
            left: createBorder(false, 0),
          };
          break;
          
        case 'vertical-only':
          // Only left and right borders
          cell.style.borders = {
            top: createBorder(false, 0),
            right: createBorder(true, 2),
            bottom: createBorder(false, 0),
            left: createBorder(true, 2),
          };
          break;
          
        case 'inner-grid':
          // Thin borders on all sides
          cell.style.borders = {
            top: createBorder(true, 1, '#d0d0d0'),
            right: createBorder(true, 1, '#d0d0d0'),
            bottom: createBorder(true, 1, '#d0d0d0'),
            left: createBorder(true, 1, '#d0d0d0'),
          };
          break;
      }
    }
    
    pushToHistory(newConfig);
    toast.success(t('table.borders.presetApplied').replace('{preset}', preset));
  }, [headerCellSelection, tableConfig, pushToHistory, t]);
  
  // Open border areas dialog
  const handleOpenBorderDialog = useCallback(() => {
    setShowBorderDialog(true);
  }, []);
  
  // Apply border area configuration
  const handleApplyBorderAreas = useCallback((config: BorderAreaConfig) => {
    console.log('🎨 Applying border area config:', config);
    
    const newConfig = { ...tableConfig };
    
    // Helper to create border definition
    const createBorder = (visible: boolean, width: number, color: string, style: BorderStyle = 'solid') => ({
      visible,
      width,
      color,
      style: style as BorderStyle,
    });
    
    // 1. Apply Header Border (thick border around header area)
    if (config.enableHeaderBorder) {
      console.log('📦 Applying header area borders');
      
      // Apply to all header rows
      if (newConfig.headerRows) {
        for (const row of newConfig.headerRows) {
          for (const cell of row.cells) {
            if (!cell.style) cell.style = {};
            if (!cell.style.borders) cell.style.borders = {};
            
            // Top border (thick for first row)
            if (row === newConfig.headerRows[0]) {
              cell.style.borders.top = createBorder(true, config.headerBorderWidth, config.headerBorderColor);
            }
            
            // Bottom border (thick for last row)
            if (row === newConfig.headerRows[newConfig.headerRows.length - 1]) {
              cell.style.borders.bottom = createBorder(true, config.headerBorderWidth, config.headerBorderColor);
            }
            
            // Left border (thick for first cell)
            if (cell === row.cells[0]) {
              cell.style.borders.left = createBorder(true, config.headerBorderWidth, config.headerBorderColor);
            }
            
            // Right border (thick for last cell)
            if (cell === row.cells[row.cells.length - 1]) {
              cell.style.borders.right = createBorder(true, config.headerBorderWidth, config.headerBorderColor);
            }
          }
        }
      }
      
      // Apply to main column headers
      for (let i = 0; i < newConfig.columns.length; i++) {
        const column = newConfig.columns[i];
        if (!column.style) column.style = {};
        if (!column.style.borders) column.style.borders = {};
        
        // Top border (thick if no header rows exist)
        if (!newConfig.headerRows || newConfig.headerRows.length === 0) {
          column.style.borders.top = createBorder(true, config.headerBorderWidth, config.headerBorderColor);
        }
        
        // Bottom border (thick, separates header from data)
        column.style.borders.bottom = createBorder(true, config.headerBorderWidth, config.headerBorderColor);
        
        // Left border (thick for first column)
        if (i === 0) {
          column.style.borders.left = createBorder(true, config.headerBorderWidth, config.headerBorderColor);
        }
        
        // Right border (thick for last column)
        if (i === newConfig.columns.length - 1) {
          column.style.borders.right = createBorder(true, config.headerBorderWidth, config.headerBorderColor);
        }
      }
    }
    
    // 2. Apply Data Border (thick border around data area)
    if (config.enableDataBorder && newConfig.rows.length > 0) {
      console.log('📊 Applying data area borders');
      
      for (let rowIdx = 0; rowIdx < newConfig.rows.length; rowIdx++) {
        const row = newConfig.rows[rowIdx];
        const isFirstRow = rowIdx === 0;
        const isLastRow = rowIdx === newConfig.rows.length - 1;
        
        for (let colIdx = 0; colIdx < newConfig.columns.length; colIdx++) {
          const column = newConfig.columns[colIdx];
          const cell = row.data[column.id];
          
          if (!cell) continue;
          if (!cell.style) cell.style = {};
          if (!cell.style.borders) cell.style.borders = {};
          
          const isFirstCol = colIdx === 0;
          const isLastCol = colIdx === newConfig.columns.length - 1;
          
          // Top border (thick for first data row, unless inner grid is enabled)
          if (isFirstRow && !config.enableInnerGrid) {
            cell.style.borders.top = createBorder(true, config.dataBorderWidth, config.dataBorderColor);
          }
          
          // Bottom border (thick for last row)
          if (isLastRow) {
            cell.style.borders.bottom = createBorder(true, config.dataBorderWidth, config.dataBorderColor);
          }
          
          // Left border (thick for first column)
          if (isFirstCol) {
            cell.style.borders.left = createBorder(true, config.dataBorderWidth, config.dataBorderColor);
          }
          
          // Right border (thick for last column)
          if (isLastCol) {
            cell.style.borders.right = createBorder(true, config.dataBorderWidth, config.dataBorderColor);
          }
        }
      }
    }
    
    // 3. Apply Inner Grid (thin borders between cells)
    if (config.enableInnerGrid) {
      console.log('🔲 Applying inner grid');
      
      // Apply to all data cells
      for (const row of newConfig.rows) {
        for (let colIdx = 0; colIdx < newConfig.columns.length; colIdx++) {
          const column = newConfig.columns[colIdx];
          const cell = row.data[column.id];
          
          if (!cell) continue;
          if (!cell.style) cell.style = {};
          if (!cell.style.borders) cell.style.borders = {};
          
          // Inner grid borders (all sides, will be overridden by data border where needed)
          const isFirstCol = colIdx === 0;
          const isLastCol = colIdx === newConfig.columns.length - 1;
          
          if (!isFirstCol) {
            cell.style.borders.left = createBorder(true, config.innerGridWidth, config.innerGridColor);
          }
          if (!isLastCol) {
            cell.style.borders.right = createBorder(true, config.innerGridWidth, config.innerGridColor);
          }
          
          cell.style.borders.top = createBorder(true, config.innerGridWidth, config.innerGridColor);
          cell.style.borders.bottom = createBorder(true, config.innerGridWidth, config.innerGridColor);
        }
      }
      
      // Re-apply data borders if needed (they should override inner grid on edges)
      if (config.enableDataBorder && newConfig.rows.length > 0) {
        // First row
        const firstRow = newConfig.rows[0];
        for (const column of newConfig.columns) {
          const cell = firstRow.data[column.id];
          if (cell && cell.style && cell.style.borders) {
            cell.style.borders.top = createBorder(true, config.dataBorderWidth, config.dataBorderColor);
          }
        }
        
        // Last row
        const lastRow = newConfig.rows[newConfig.rows.length - 1];
        for (const column of newConfig.columns) {
          const cell = lastRow.data[column.id];
          if (cell && cell.style && cell.style.borders) {
            cell.style.borders.bottom = createBorder(true, config.dataBorderWidth, config.dataBorderColor);
          }
        }
        
        // First and last column
        for (const row of newConfig.rows) {
          const firstCol = newConfig.columns[0];
          const lastCol = newConfig.columns[newConfig.columns.length - 1];
          
          const firstCell = row.data[firstCol.id];
          if (firstCell && firstCell.style && firstCell.style.borders) {
            firstCell.style.borders.left = createBorder(true, config.dataBorderWidth, config.dataBorderColor);
          }
          
          const lastCell = row.data[lastCol.id];
          if (lastCell && lastCell.style && lastCell.style.borders) {
            lastCell.style.borders.right = createBorder(true, config.dataBorderWidth, config.dataBorderColor);
          }
        }
      }
    }
    
    // 4. Apply Zebra Stripes (alternating row colors)
    if (config.enableZebraStripes) {
      console.log('🦓 Applying zebra stripes');
      
      for (let rowIdx = 0; rowIdx < newConfig.rows.length; rowIdx++) {
        const row = newConfig.rows[rowIdx];
        const isEvenRow = rowIdx % 2 === 0;
        
        for (const column of newConfig.columns) {
          const cell = row.data[column.id];
          if (!cell) continue;
          
          if (!cell.style) cell.style = {};
          
          // Apply alternating background color
          cell.style.backgroundColor = isEvenRow ? '#ffffff' : '#f8f8f8';
        }
      }
    }
    
    pushToHistory(newConfig);
    setShowBorderDialog(false);
    toast.success(t('table.borders.formatted'));
  }, [tableConfig, pushToHistory, t]);

  // ===== HEADER CELL SELECTION & MERGE/SPLIT =====
  
  // Toggle header cell selection
  const handleHeaderCellClick = useCallback((rowId: string, cellId: string, ctrlKey: boolean) => {
    setHeaderCellSelection(prev => {
      const exists = prev.some(sel => sel.rowId === rowId && sel.cellId === cellId);
      
      if (ctrlKey) {
        // Multi-select: toggle
        if (exists) {
          return prev.filter(sel => !(sel.rowId === rowId && sel.cellId === cellId));
        } else {
          return [...prev, { rowId, cellId }];
        }
      } else {
        // Single select: replace
        if (exists && prev.length === 1) {
          return []; // Deselect if clicking the same cell
        }
        return [{ rowId, cellId }];
      }
    });
  }, []);
  
  // Double-click header cell to edit
  const handleHeaderCellDoubleClick = useCallback((rowId: string, cellId: string) => {
    setEditingHeaderCell({ rowId, cellId });
    setHeaderCellSelection([]); // Clear selection when editing
  }, []);
  
  // Merge selected header cells (horizontal or vertical)
  const handleMergeHeaderCells = useCallback(() => {
    if (headerCellSelection.length < 2) {
      toast.error(t('table.cells.minTwo'));
      return;
    }
    
    const newConfig = { ...tableConfig };
    if (!newConfig.headerRows) {
      newConfig.headerRows = [];
    }
    
    // Check if __main_columns__ is in the selection
    const hasMainColumns = headerCellSelection.some(sel => sel.rowId === '__main_columns__');
    
    // Determine if selection is horizontal (same row) or vertical (same column)
    const uniqueRows = new Set(headerCellSelection.map(sel => sel.rowId));
    const isHorizontal = uniqueRows.size === 1;
    
    if (isHorizontal) {
      // HORIZONTAL MERGE (only within headerRows or __main_columns__, not mixed)
      const rowId = headerCellSelection[0].rowId;
      
      if (rowId === '__main_columns__') {
        toast.error(t('table.cells.horizontalMainError'));
        return;
      }
      
      const rowIndex = newConfig.headerRows.findIndex(row => row.id === rowId);
      if (rowIndex === -1) return;
      
      const row = newConfig.headerRows[rowIndex];
      const selectedCellIds = new Set(headerCellSelection.map(sel => sel.cellId));
      const selectedCells = row.cells.filter(cell => selectedCellIds.has(cell.id));
      
      // Check if cells are adjacent
      const cellIndices = selectedCells.map(cell => row.cells.indexOf(cell)).sort((a, b) => a - b);
      for (let i = 1; i < cellIndices.length; i++) {
        if (cellIndices[i] !== cellIndices[i - 1] + 1) {
          toast.error(t('table.cells.notAdjacent'));
          return;
        }
      }
      
      // Merge cells: keep first, sum colspan, remove others
      const firstCellIndex = cellIndices[0];
      const firstCell = row.cells[firstCellIndex];
      const totalColspan = selectedCells.reduce((sum, cell) => sum + (cell.colspan || 1), 0);
      const combinedLabel = selectedCells.map(c => c.label).filter(Boolean).join(' ');
      
      // Create merged cell
      const mergedCell = {
        ...firstCell,
        colspan: totalColspan,
        label: combinedLabel || firstCell.label,
      };
      
      // Remove merged cells and insert new one
      const newCells = row.cells.filter(cell => !selectedCellIds.has(cell.id) || cell.id === firstCell.id);
      const mergedCellFinalIndex = newCells.findIndex(cell => cell.id === firstCell.id);
      newCells[mergedCellFinalIndex] = mergedCell;
      
      newConfig.headerRows[rowIndex] = {
        ...row,
        cells: newCells,
      };
      
      pushToHistory(newConfig);
      setHeaderCellSelection([]);
      toast.success(t('table.cells.mergedHorizontal'));
      
    } else {
      // VERTICAL MERGE (can include __main_columns__)
      
      // Helper to get column index for any selection
      const getColumnIndex = (sel: typeof headerCellSelection[0]): number => {
        if (sel.rowId === '__main_columns__') {
          return newConfig.columns.findIndex(col => col.id === sel.cellId);
        } else {
          const rowIdx = newConfig.headerRows!.findIndex(r => r.id === sel.rowId);
          if (rowIdx === -1) return -1;
          const row = newConfig.headerRows![rowIdx];
          const cell = row.cells.find(c => c.id === sel.cellId);
          return cell?.columnStartIndex ?? -1;
        }
      };
      
      // Group selections by column
      const columnGroups = new Map<number, typeof headerCellSelection>();
      
      headerCellSelection.forEach(sel => {
        const colIdx = getColumnIndex(sel);
        if (colIdx === -1) return;
        
        if (!columnGroups.has(colIdx)) {
          columnGroups.set(colIdx, []);
        }
        columnGroups.get(colIdx)!.push(sel);
      });
      
      // Must all be in the same column
      if (columnGroups.size > 1) {
        toast.error(t('table.cells.verticalSameColumn'));
        return;
      }
      
      const columnIndex = Array.from(columnGroups.keys())[0];
      
      // Sort selections by their position (headerRows first, then __main_columns__)
      const sortedSelections = [...headerCellSelection].sort((a, b) => {
        if (a.rowId === '__main_columns__') return 1; // __main_columns__ goes last
        if (b.rowId === '__main_columns__') return -1;
        
        const rowIdxA = newConfig.headerRows!.findIndex(r => r.id === a.rowId);
        const rowIdxB = newConfig.headerRows!.findIndex(r => r.id === b.rowId);
        return rowIdxA - rowIdxB;
      });
      
      // Separate headerRow and __main_columns__ selections
      const headerRowSelections = headerCellSelection.filter(sel => sel.rowId !== '__main_columns__');
      
      // Get row indices for headerRow selections
      const headerRowIndices = headerRowSelections.map(sel => 
        newConfig.headerRows!.findIndex(r => r.id === sel.rowId)
      );
      
      // Validate indices
      if (headerRowIndices.some(idx => idx === -1)) {
        toast.error(t('table.cells.invalidSelection'));
        return;
      }
      
      // Check if headerRows are adjacent
      if (headerRowIndices.length > 1) {
        const sortedIndices = [...headerRowIndices].sort((a, b) => a - b);
        for (let i = 1; i < sortedIndices.length; i++) {
          if (sortedIndices[i] !== sortedIndices[i - 1] + 1) {
            toast.error(t('table.cells.notAdjacentVertical'));
            return;
          }
        }
      }
      
      // If __main_columns__ is included, check that the last headerRow is adjacent to it
      if (hasMainColumns && headerRowIndices.length > 0) {
        const lastHeaderRowIdx = Math.max(...headerRowIndices);
        const numHeaderRows = newConfig.headerRows.length;
        
        if (lastHeaderRowIdx !== numHeaderRows - 1) {
          toast.error(t('table.cells.mainMergeError'));
          return;
        }
      }
      
      // NOW PERFORM THE MERGE
      
      if (hasMainColumns) {
        // MERGE WITH __main_columns__
        
        // Find the topmost headerRow cell
        const firstSel = sortedSelections[0];
        const firstRowIdx = newConfig.headerRows!.findIndex(r => r.id === firstSel.rowId);
        const firstRow = newConfig.headerRows[firstRowIdx];
        const firstCell = firstRow.cells.find(c => c.id === firstSel.cellId);
        
        if (!firstCell) {
          toast.error(t('table.cells.cellNotFound'));
          return;
        }
        
        // Combine all labels (including main column label)
        const mainColumn = newConfig.columns[columnIndex];
        const labels = sortedSelections.map(sel => {
          if (sel.rowId === '__main_columns__') {
            return mainColumn?.label || '';
          } else {
            const rowIdx = newConfig.headerRows!.findIndex(r => r.id === sel.rowId);
            const row = newConfig.headerRows![rowIdx];
            const cell = row.cells.find(c => c.id === sel.cellId);
            return cell?.label || '';
          }
        }).filter(Boolean);
        
        const combinedLabel = labels.join(' ');
        
        // Calculate rowspan: number of headerRows + 1 for __main_columns__
        const rowspan = headerRowSelections.length + 1;
        
        // Update first cell with rowspan
        const firstCellIdx = firstRow.cells.findIndex(c => c.id === firstCell.id);
        newConfig.headerRows[firstRowIdx].cells[firstCellIdx] = {
          ...firstCell,
          rowspan,
          label: combinedLabel || firstCell.label,
        };
        
        // Remove cells from subsequent headerRows
        for (let i = 1; i < headerRowSelections.length; i++) {
          const sel = headerRowSelections[i];
          const rowIdx = newConfig.headerRows!.findIndex(r => r.id === sel.rowId);
          if (rowIdx !== -1 && newConfig.headerRows[rowIdx].cells) {
            newConfig.headerRows[rowIdx].cells = newConfig.headerRows[rowIdx].cells.filter(
              c => c.id !== sel.cellId
            );
          }
        }
        
        // Mark main column as covered by rowspan
        newConfig.columns[columnIndex] = {
          ...newConfig.columns[columnIndex],
          coveredByRowspan: true,
          coveredByHeaderCellId: firstCell.id,
        };
        
        console.log('✅ Merged headerRow cells with __main_columns__', {
          columnIndex,
          rowspan,
          combinedLabel,
        });
        
        pushToHistory(newConfig);
        setHeaderCellSelection([]);
        toast.success(t('table.cells.mergedMain'));
        
      } else {
        // MERGE WITHIN headerRows ONLY (existing logic)
        
        const firstRowIdx = headerRowIndices[0];
        const firstSel = sortedSelections[0];
        const firstRow = newConfig.headerRows[firstRowIdx];
        if (!firstRow || !firstRow.cells) return;
        
        const firstCell = firstRow.cells.find(c => c.id === firstSel.cellId);
        if (!firstCell) return;
        
        // Combine labels
        const combinedLabel = sortedSelections
          .map(sel => {
            const rowIdx = newConfig.headerRows!.findIndex(r => r.id === sel.rowId);
            if (rowIdx === -1) return '';
            
            const row = newConfig.headerRows![rowIdx];
            if (!row || !row.cells) return '';
            
            const cell = row.cells.find(c => c.id === sel.cellId);
            return cell?.label || '';
          })
          .filter(Boolean)
          .join(' ');
        
        // Update first cell with rowspan
        const firstCellIdx = firstRow.cells.findIndex(c => c.id === firstCell.id);
        newConfig.headerRows[firstRowIdx].cells[firstCellIdx] = {
          ...firstCell,
          rowspan: headerRowIndices.length,
          label: combinedLabel || firstCell.label,
        };
        
        // Remove cells from subsequent rows
        for (let i = 1; i < headerRowIndices.length; i++) {
          const rowIdx = headerRowIndices[i];
          const sel = sortedSelections[i];
          if (newConfig.headerRows[rowIdx] && newConfig.headerRows[rowIdx].cells) {
            newConfig.headerRows[rowIdx].cells = newConfig.headerRows[rowIdx].cells.filter(
              c => c.id !== sel.cellId
            );
          }
        }
        
        pushToHistory(newConfig);
        setHeaderCellSelection([]);
        toast.success(t('table.cells.mergedVertical'));
      }
    }
  }, [headerCellSelection, tableConfig, pushToHistory, t]);
  
  // Split merged header cell (horizontal or vertical)
  const handleSplitHeaderCells = useCallback(() => {
    if (headerCellSelection.length !== 1) {
      toast.error(t('table.cells.singleMerged'));
      return;
    }
    
    const { rowId, cellId } = headerCellSelection[0];
    const newConfig = { ...tableConfig };
    
    if (!newConfig.headerRows) return;
    
    const rowIndex = newConfig.headerRows.findIndex(row => row.id === rowId);
    if (rowIndex === -1) return;
    
    const row = newConfig.headerRows[rowIndex];
    const cellIndex = row.cells.findIndex(cell => cell.id === cellId);
    if (cellIndex === -1) return;
    
    const cell = row.cells[cellIndex];
    
    const hasColspan = (cell.colspan || 1) > 1;
    const hasRowspan = (cell.rowspan || 1) > 1;
    
    if (!hasColspan && !hasRowspan) {
      toast.error(t('table.cells.notMerged'));
      return;
    }
    
    // HORIZONTAL SPLIT (colspan)
    if (hasColspan && !hasRowspan) {
      const newCells: typeof row.cells = [];
      row.cells.forEach((c, idx) => {
        if (idx === cellIndex) {
          // Split this cell horizontally
          for (let i = 0; i < (cell.colspan || 1); i++) {
            newCells.push({
              id: `${cell.id}-split-${i}`,
              label: i === 0 ? cell.label : '',
              colspan: 1,
              columnStartIndex: cell.columnStartIndex + i,
            });
          }
        } else {
          newCells.push(c);
        }
      });
      
      newConfig.headerRows[rowIndex] = {
        ...row,
        cells: newCells,
      };
      
      pushToHistory(newConfig);
      setHeaderCellSelection([]);
      toast.success(t('table.cells.splitHorizontal'));
      
    } else if (hasRowspan && !hasColspan) {
      // VERTICAL SPLIT (rowspan)
      const rowspan = cell.rowspan || 1;
      
      // Check if this cell covers __main_columns__
      const numHeaderRows = newConfig.headerRows.length;
      const coversMainColumns = (rowIndex + rowspan) > numHeaderRows;
      
      // Reset rowspan on the original cell
      newConfig.headerRows[rowIndex].cells[cellIndex] = {
        ...cell,
        rowspan: 1,
      };
      
      // Add cells back to subsequent headerRows
      const headerRowsToRestore = coversMainColumns ? rowspan - 1 : rowspan;
      
      for (let i = 1; i < headerRowsToRestore; i++) {
        const targetRowIdx = rowIndex + i;
        if (targetRowIdx >= numHeaderRows) break; // Don't add cells beyond headerRows
        
        // Insert a new cell at the same column position
        const newCell = {
          id: `${cell.id}-split-${i}`,
          label: '',
          colspan: 1,
          columnStartIndex: cell.columnStartIndex,
        };
        
        // Find the correct position to insert (based on columnStartIndex)
        const targetRow = newConfig.headerRows[targetRowIdx];
        let insertIdx = 0;
        for (let j = 0; j < targetRow.cells.length; j++) {
          if (targetRow.cells[j].columnStartIndex < cell.columnStartIndex) {
            insertIdx = j + 1;
          }
        }
        
        newConfig.headerRows[targetRowIdx].cells.splice(insertIdx, 0, newCell);
      }
      
      // If this cell covered __main_columns__, restore the main column
      if (coversMainColumns) {
        const columnIndex = cell.columnStartIndex;
        if (columnIndex >= 0 && columnIndex < newConfig.columns.length) {
          newConfig.columns[columnIndex] = {
            ...newConfig.columns[columnIndex],
            coveredByRowspan: false,
            coveredByHeaderCellId: undefined,
          };
          console.log('✅ Restored __main_columns__ column:', columnIndex);
        }
      }
      
      pushToHistory(newConfig);
      setHeaderCellSelection([]);
      toast.success(t('table.cells.splitVertical'));
      
    } else {
      // Both colspan AND rowspan - not supported yet
      toast.error(t('table.cells.split2DError'));
    }
  }, [headerCellSelection, tableConfig, pushToHistory, t]);
  
  // Update header cell value
  const handleHeaderCellValueChange = useCallback((rowId: string, cellId: string, newValue: string) => {
    const newConfig = { ...tableConfig };
    
    if (!newConfig.headerRows) return;
    
    const rowIndex = newConfig.headerRows.findIndex(row => row.id === rowId);
    if (rowIndex === -1) return;
    
    const row = newConfig.headerRows[rowIndex];
    const cellIndex = row.cells.findIndex(cell => cell.id === cellId);
    if (cellIndex === -1) return;
    
    // Update cell label
    newConfig.headerRows[rowIndex] = {
      ...row,
      cells: row.cells.map((cell, idx) => 
        idx === cellIndex ? { ...cell, label: newValue } : cell
      ),
    };
    
    pushToHistory(newConfig);
  }, [tableConfig, pushToHistory]);

  // Column resize - now with history support for Ctrl+Z
  const handleColumnResize = useCallback((columnId: string, newWidth: number) => {
    console.log('🔧 handleColumnResize called:', { columnId, newWidth });
    
    const newConfig = {
      ...tableConfig,
      columns: tableConfig.columns.map(col => 
        col.id === columnId ? { ...col, width: newWidth } : col
      ),
    };
    
    console.log('🔧 newConfig.columns:', newConfig.columns.map(c => ({ id: c.id, width: c.width })));
    
    // Push to history so Ctrl+Z works
    pushToHistory(newConfig);
  }, [tableConfig, pushToHistory]);

  // Column reorder - move column from one index to another
  const handleColumnReorder = useCallback((fromIndex: number, toIndex: number) => {
    console.log('🔵 handleColumnReorder called:', { fromIndex, toIndex });
    
    const newConfig = { ...tableConfig };
    const columns = [...newConfig.columns];
    
    // Remove column from old position
    const [movedColumn] = columns.splice(fromIndex, 1);
    
    // Insert at new position
    columns.splice(toIndex, 0, movedColumn);
    
    // Update column indices
    columns.forEach((col, idx) => {
      col.index = idx;
    });
    
    newConfig.columns = columns;
    
    console.log('✅ Columns reordered:', columns.map(c => c.label));
    
    pushToHistory(newConfig);
  }, [tableConfig, pushToHistory]);

  // ===== NEW: RENAME COLUMN =====
  const handleRenameColumn = useCallback((newName: string) => {
    if (!headerSelection || headerSelection.length !== 1) return;

    console.log('🏷️ Renaming column:', headerSelection, 'to:', newName);

    const newConfig = { ...tableConfig };
    const columnIndex = headerSelection[0];
    const column = newConfig.columns[columnIndex];
    
    // Update column label
    newConfig.columns[columnIndex] = {
      ...column,
      label: newName,
    };

    // Update cells in all rows
    newConfig.rows = newConfig.rows.map(row => {
      const oldCellId = `${row.id}-${column.attributeKey}`;
      const newCellId = `${row.id}-${newName}`;
      
      const newData: typeof row.data = {};
      Object.entries(row.data).forEach(([key, cell]) => {
        if (cell.id === oldCellId) {
          newData[newName] = { ...cell, id: newCellId };
        } else {
          newData[key] = cell;
        }
      });

      return { ...row, data: newData };
    });

    console.log('✅ Column renamed successfully');
    pushToHistory(newConfig);
    setShowRenameDialog(false);
    setHeaderSelection(null);
  }, [headerSelection, tableConfig, pushToHistory]);

  // ===== NEW: MERGE COLUMNS =====
  const handleMergeColumns = useCallback((displayName: string) => {
    if (!headerSelection || headerSelection.length < 2) return;

    console.log('🔀 Merging columns:', headerSelection, 'into:', displayName);

    const newConfig = { ...tableConfig };
    const selectedColumns = headerSelection.map(idx => newConfig.columns[idx]);
    
    const mergedAttributeKeys = selectedColumns.map(col => col.attributeKey).filter(Boolean) as string[];
    
    console.log('🔍 Merging attributes:', mergedAttributeKeys);
    
    // Create merged column
    const mergedColumnIds = selectedColumns.map(col => col.id);
    const firstColumnIndex = Math.min(...headerSelection);
    
    const mergedColumn = {
      id: `merged-${Date.now()}`,
      index: firstColumnIndex,
      label: displayName,
      dataType: 'string' as const,
      width: selectedColumns.reduce((sum, col) => sum + col.width, 0),
      isMerged: true,
      mergedFrom: mergedAttributeKeys,
      mergedColumnIds: mergedColumnIds,
    };

    // Remove old columns and insert merged column
    const remainingColumns = newConfig.columns.filter((_, idx) => !headerSelection.includes(idx));
    remainingColumns.splice(firstColumnIndex, 0, mergedColumn);
    
    // Update indices
    remainingColumns.forEach((col, idx) => {
      col.index = idx;
    });
    
    newConfig.columns = remainingColumns;

    // Update rows - merge cell values with SUM for numeric values
    newConfig.rows = newConfig.rows.map((row, rowIdx) => {
      if (row.isHeader) return row;

      // Collect values from all merged columns
      const values = mergedAttributeKeys
        .map(attrKey => row.allAttributes?.[attrKey])
        .filter(val => val !== null && val !== undefined && val !== '');

      // Check if all values are numeric
      const numericValues = values
        .map(v => typeof v === 'number' ? v : parseFloat(String(v)))
        .filter(v => !isNaN(v));

      let mergedValue: string | number = '';
      
      if (numericValues.length > 0 && numericValues.length === values.length) {
        // All values are numeric - SUM them!
        mergedValue = numericValues.reduce((sum, val) => sum + val, 0);
        console.log(`📊 Row ${rowIdx + 1}: Summing values:`, numericValues, '→', mergedValue);
      } else {
        // Non-numeric or mixed - take first non-empty value
        mergedValue = values[0] || '';
        console.log(`📝 Row ${rowIdx + 1}: Using first value:`, mergedValue);
      }
      
      const newData = { ...row.data };
      
      // Remove old cells
      mergedColumnIds.forEach(colId => {
        delete newData[colId];
      });

      // Add merged cell
      newData[mergedColumn.id] = {
        id: `${row.id}-${mergedColumn.id}`,
        rowIndex: rowIdx,
        colIndex: firstColumnIndex,
        value: mergedValue,
        dataType: typeof mergedValue === 'number' ? 'number' : 'string',
        isReadOnly: false,
      };

      return { ...row, data: newData };
    });

    console.log('✅ Columns merged successfully with SUM for numeric values');
    pushToHistory(newConfig);
    setShowMergeDialog(false);
    setHeaderSelection(null);
  }, [headerSelection, tableConfig, pushToHistory]);

  // Header management functions removed
  const handleMergeHeaderCells_REMOVED = useCallback(() => {
    if (!headerSelection || headerSelection.length < 2) return;

    console.log('🔗 Merging header cells:', headerSelection);

    // Find the header cell with content
    const selectedColumns = headerSelection.map(idx => tableConfig.columns[idx]);
    const labelColumn = selectedColumns.find(col => col.label && col.label.trim() !== '');
    const mergedLabel = labelColumn?.label || selectedColumns[0]?.label || '';

    console.log('📌 Using label:', mergedLabel);

    const newConfig = { ...tableConfig };
    const firstColumnIndex = Math.min(...headerSelection);
    
    // Mark the first column as having a header merge
    newConfig.columns[firstColumnIndex] = {
      ...newConfig.columns[firstColumnIndex],
      headerColSpan: headerSelection.length,
      headerLabel: mergedLabel,
    };

    // Mark other columns as having their header merged
    for (let i = 1; i < headerSelection.length; i++) {
      const colIdx = headerSelection[i];
      newConfig.columns[colIdx] = {
        ...newConfig.columns[colIdx],
        headerMergedInto: newConfig.columns[firstColumnIndex].id,
      };
    }

    console.log('✅ Header cells merged successfully');
    pushToHistory(newConfig);
    setHeaderSelection(null);
  }, [headerSelection, tableConfig, pushToHistory]);

  // ===== ADD HEADER GROUP (using old working system) =====
  const handleAddHeaderGroup = useCallback((label: string) => {
    if (!headerSelection || headerSelection.length < 1) return;

    console.log('📂 Adding header group:', label);
    console.log('📊 Header selection (indices):', headerSelection);

    const newConfig = { ...tableConfig };
    
    // Initialize headerGroups if not exists
    if (!newConfig.headerGroups) {
      newConfig.headerGroups = [];
    }

    // Find the highest level to place the new group on top
    const maxLevel = newConfig.headerGroups.length > 0
      ? Math.max(...newConfig.headerGroups.map(g => g.level))
      : -1;

    // Store column IDs instead of indices
    const columnIds = headerSelection
      .sort((a, b) => a - b)
      .map(idx => newConfig.columns[idx].id);

    const newGroup: HeaderGroup = {
      id: `group-${Date.now()}`,
      label,
      columnIds,
      level: maxLevel + 1,
    };

    newConfig.headerGroups.push(newGroup);

    console.log('✅ Header group added successfully:', newGroup);
    
    pushToHistory(newConfig);
    setShowRenameDialog(false);
    setHeaderSelection(null);
  }, [headerSelection, tableConfig, pushToHistory]);

  // ===== NEW: DELETE HEADER GROUP =====
  const handleDeleteHeaderGroup = useCallback((groupId: string) => {
    console.log('🗑️ Deleting header group:', groupId);

    const newConfig = { ...tableConfig };
    
    if (!newConfig.headerGroups) return;

    // Remove the group
    newConfig.headerGroups = newConfig.headerGroups.filter(g => g.id !== groupId);

    // Re-calculate levels to avoid gaps
    newConfig.headerGroups.sort((a, b) => b.level - a.level); // Sort by level descending
    newConfig.headerGroups.forEach((group, idx) => {
      group.level = newConfig.headerGroups.length - 1 - idx;
    });

    console.log('✅ Header group deleted successfully');
    pushToHistory(newConfig);
  }, [tableConfig, pushToHistory]);

  // ===== NEW: RESET ALL HEADERS =====
  const handleResetHeaders = useCallback(() => {
    console.log('🗑️ Resetting all custom header rows...');
    console.log('📊 BEFORE reset - Full tableConfig:', JSON.parse(JSON.stringify(tableConfig)));
    console.log('📊 BEFORE reset - Columns:', tableConfig.columns.map(c => ({ 
      id: c.id, 
      label: c.label, 
      attributeKey: c.attributeKey,
      skipHeaderRows: (c as any).skipHeaderRows, 
      headerColSpan: (c as any).headerColSpan,
      coveredByRowspan: (c as any).coveredByRowspan 
    })));

    const newConfig = { ...tableConfig };
    
    // 1. Remove all custom headerRows
    newConfig.headerRows = [];
    
    // 2. Remove all headerGroups
    newConfig.headerGroups = [];
    
    // 3. Reset column header properties (merge props + borders, but keep all other column properties!)
    newConfig.columns = newConfig.columns.map((col, idx) => {
      // Keep all existing properties except header-merge related ones
      const cleanedCol = { ...col };
      
      // Remove header-merge properties
      delete (cleanedCol as any).skipHeaderRows;
      delete (cleanedCol as any).headerColSpan;
      delete (cleanedCol as any).headerRowSpan;
      delete (cleanedCol as any).coveredByRowspan;
      
      // Remove custom borders from column header style
      if (cleanedCol.style?.borders) {
        const { borders, ...restStyle } = cleanedCol.style;
        cleanedCol.style = restStyle;
      }
      
      // DEFENSIVE: Ensure column is visible and has correct index
      cleanedCol.isVisible = true;
      cleanedCol.index = idx;
      
      // RESET: Restore original attribute name as label
      if (cleanedCol.attributeKey) {
        console.log(`🔄 Resetting column label from "${cleanedCol.label}" to "${cleanedCol.attributeKey}"`);
        cleanedCol.label = cleanedCol.attributeKey;
      } else if (!cleanedCol.label) {
        console.warn(`⚠️ Column ${cleanedCol.id} has no label and no attributeKey!`);
        cleanedCol.label = '(Unbenannt)';
      }
      
      // DEFENSIVE: Ensure column has a minimum width (prevent invisible columns)
      if (!cleanedCol.width || cleanedCol.width < 50) {
        console.warn(`⚠️ Column ${cleanedCol.id} has invalid width (${cleanedCol.width})! Setting to 150px`);
        cleanedCol.width = 150;
      }
      
      return cleanedCol;
    });
    
    console.log('📊 AFTER reset - Columns:', newConfig.columns.map(c => ({ 
      id: c.id, 
      label: c.label, 
      attributeKey: c.attributeKey, 
      isVisible: c.isVisible,
      skipHeaderRows: (c as any).skipHeaderRows, 
      headerColSpan: (c as any).headerColSpan,
      coveredByRowspan: (c as any).coveredByRowspan 
    })));
    console.log('📊 AFTER reset - Full newConfig:', JSON.parse(JSON.stringify(newConfig)));
    
    // 4. Reset data cell borders to default
    console.log('🔲 Resetting all data cell borders...');
    newConfig.rows = newConfig.rows.map(row => {
      if (row.isHeader) return row;
      
      const newData: typeof row.data = {};
      Object.entries(row.data).forEach(([colId, cell]) => {
        newData[colId] = {
          ...cell,
          style: {
            borderTop: '1px solid #000000',
            borderRight: '1px solid #000000',
            borderBottom: '1px solid #000000',
            borderLeft: '1px solid #000000',
          },
        };
      });
      
      return { ...row, data: newData };
    });
    
    console.log('✅ All custom headers and borders reset successfully');
    toast.success(t('table.borders.reset'));
    pushToHistory(newConfig);
  }, [tableConfig, pushToHistory, t]);

  // ===== NEW: HEADER CELL EDITING =====
  // NOTE: headerCellSelection state is already declared earlier (line ~171)
  
  const handleHeaderCellSelectionChange = useCallback((selections: HeaderCellSelection[]) => {
    setHeaderCellSelection(selections);
    console.log('🟦 Header cell selection changed:', selections);
  }, []);
  
  const handleHeaderCellEdit = useCallback((rowId: string, cellId: string, newLabel: string) => {
    console.log('✏️ Editing header cell:', { rowId, cellId, newLabel });
    
    const newConfig = { ...tableConfig };
    
    // Handle main column header row
    if (rowId === '__main_columns__') {
      const column = newConfig.columns.find(c => c.id === cellId);
      if (!column) return;
      
      column.label = newLabel;
      
      console.log('✅ Main column label updated');
      pushToHistory(newConfig);
      toast.success(t('table.columns.renamed'));
      return;
    }
    
    if (!newConfig.headerRows) return;
    
    const row = newConfig.headerRows.find(r => r.id === rowId);
    if (!row) return;
    
    const cell = row.cells.find(c => c.id === cellId);
    if (!cell) return;
    
    // Update the cell label
    cell.label = newLabel;
    
    console.log('✅ Header cell label updated');
    pushToHistory(newConfig);
    toast.success(t('table.headers.updated'));
  }, [tableConfig, pushToHistory, t]);
  
  const handleHeaderCellMerge = useCallback((selections: HeaderCellSelection[]) => {
    console.log('🔗 Merging header cells:', selections);
    
    if (selections.length < 2) {
      toast.error(t('table.cells.minTwo'));
      return;
    }
    
    const newConfig = { ...tableConfig };
    
    // Check if all selections are from main column header row
    const allMainColumns = selections.every(sel => sel.rowId === '__main_columns__');
    const anyMainColumns = selections.some(sel => sel.rowId === '__main_columns__');
    
    // Don't allow mixing main columns with headerRows
    if (anyMainColumns && !allMainColumns) {
      toast.error(t('table.cells.mainMixError'));
      return;
    }
    
    if (allMainColumns) {
      // Merge main column headers
      console.log('🔗 Merging MAIN column headers');
      
      // Sort by cell index
      const sortedSels = [...selections].sort((a, b) => a.cellIndex - b.cellIndex);
      
      // Check if columns are consecutive
      for (let i = 1; i < sortedSels.length; i++) {
        if (sortedSels[i].cellIndex !== sortedSels[i-1].cellIndex + 1) {
          toast.error(t('table.cells.notAdjacent'));
          return;
        }
      }
      
      // Get the master column (first selected)
      const masterColumn = newConfig.columns.find(c => c.id === sortedSels[0].cellId);
      if (!masterColumn) return;
      
      // Calculate total colspan
      const totalColspan = selections.length;
      
      // Set master column's colspan
      masterColumn.headerColSpan = totalColspan;
      
      // Mark other selected columns as merged (headerColSpan=0)
      for (let i = 1; i < sortedSels.length; i++) {
        const col = newConfig.columns.find(c => c.id === sortedSels[i].cellId);
        if (col) {
          col.headerColSpan = 0; // Mark as merged into master
        }
      }
      
      console.log('✅ Main column headers merged:', {
        masterColumn: masterColumn.id,
        colspan: totalColspan,
      });
      
      pushToHistory(newConfig);
      toast.success(t('table.columns.merged').replace('{count}', totalColspan.toString()));
      return;
    }
    
    if (!newConfig.headerRows) return;
    
    // Group selections by row
    const byRow = new Map<string, typeof selections>();
    selections.forEach(sel => {
      const existing = byRow.get(sel.rowId) || [];
      byRow.set(sel.rowId, [...existing, sel]);
    });
    
    // Check if all selections are in the same row (horizontal merge)
    if (byRow.size === 1) {
      // Horizontal merge
      const [rowId, cells] = Array.from(byRow.entries())[0];
      
      // Safety check: should not happen due to check above, but be safe
      if (rowId === '__main_columns__') {
        toast.error('Internal Error: Main columns should be handled');
        return;
      }
      
      const row = newConfig.headerRows.find(r => r.id === rowId);
      if (!row) return;
      
      // Sort by cell index
      cells.sort((a, b) => a.cellIndex - b.cellIndex);
      
      // Check if cells are consecutive
      for (let i = 1; i < cells.length; i++) {
        if (cells[i].cellIndex !== cells[i-1].cellIndex + 1) {
          toast.error(t('table.cells.notAdjacent'));
          return;
        }
      }
      
      const firstCell = row.cells.find(c => c.id === cells[0].cellId);
      if (!firstCell) return;
      
      // Calculate new colspan
      const totalColspan = cells.reduce((sum, sel) => {
        const cell = row.cells.find(c => c.id === sel.cellId);
        return sum + (cell?.colspan || 1);
      }, 0);
      
      // Merge the cells
      firstCell.colspan = totalColspan;
      
      // Remove the other cells
      for (let i = 1; i < cells.length; i++) {
        const idx = row.cells.findIndex(c => c.id === cells[i].cellId);
        if (idx !== -1) {
          row.cells.splice(idx, 1);
        }
      }
      
      console.log('✅ Horizontal merge successful');
      pushToHistory(newConfig);
      toast.success(t('table.cells.mergedHorizontal'));
      
    } else {
      // Vertical or 2D merge
      
      // Safety check: Main columns should not be here
      if (selections.some(sel => sel.rowId === '__main_columns__')) {
        toast.error(t('table.cells.mainVerticalError'));
        return;
      }
      
      // Sort selections by row level and cell index to find the top-left cell
      const sortedSelections = [...selections].sort((a, b) => {
        if (a.rowLevel !== b.rowLevel) return a.rowLevel - b.rowLevel;
        return a.cellIndex - b.cellIndex;
      });
      
      // Find unique row levels and cell indices
      const rowLevels = [...new Set(selections.map(s => s.rowLevel))].sort((a, b) => a - b);
      const cellIndices = [...new Set(selections.map(s => s.cellIndex))].sort((a, b) => a - b);
      
      // Check if selections form a rectangle
      const expectedCount = rowLevels.length * cellIndices.length;
      if (selections.length !== expectedCount) {
        toast.error(t('table.cells.rectangleError'));
        return;
      }
      
      // Validate that all cells in the rectangle are selected
      for (const rowLevel of rowLevels) {
        for (const cellIdx of cellIndices) {
          const found = selections.find(s => s.rowLevel === rowLevel && s.cellIndex === cellIdx);
          if (!found) {
            toast.error(t('table.cells.rectangleError'));
            return;
          }
        }
      }
      
      // Get the master cell (top-left)
      const masterSel = sortedSelections[0];
      const masterRow = newConfig.headerRows[masterSel.rowLevel];
      if (!masterRow) return;
      
      const masterCell = masterRow.cells.find(c => c.id === masterSel.cellId);
      if (!masterCell) return;
      
      // Calculate total colspan (width) - use the first row to calculate
      const firstRowCells = selections.filter(s => s.rowLevel === rowLevels[0]);
      const totalColspan = firstRowCells.reduce((sum, sel) => {
        const row = newConfig.headerRows[sel.rowLevel];
        const cell = row?.cells.find(c => c.id === sel.cellId);
        return sum + (cell?.colspan || 1);
      }, 0);
      
      // Set master cell's colspan and rowspan
      masterCell.colspan = totalColspan;
      masterCell.rowspan = rowLevels.length;
      
      console.log('🎯 Master cell 2D merge:', {
        id: masterCell.id,
        colspan: masterCell.colspan,
        rowspan: masterCell.rowspan,
      });
      
      // Remove all other selected cells
      for (const sel of selections) {
        // Skip the master cell
        if (sel.rowId === masterSel.rowId && sel.cellId === masterSel.cellId) continue;
        
        const row = newConfig.headerRows.find(r => r.id === sel.rowId);
        if (!row) continue;
        
        const cellIdx = row.cells.findIndex(c => c.id === sel.cellId);
        if (cellIdx !== -1) {
          // Mark as merged (colspan=0) instead of removing, so we can track it
          row.cells[cellIdx].colspan = 0;
          console.log(`  🔗 Merged cell ${sel.cellId} into master (colspan=0)`);
        }
      }
      
      console.log('✅ 2D merge successful');
      pushToHistory(newConfig);
      toast.success(t('table.cells.merged2D').replace('{width}', totalColspan.toString()).replace('{height}', rowLevels.length.toString()));
    }
  }, [tableConfig, pushToHistory, t]);
  
  const handleHeaderCellUnmerge = useCallback((rowId: string, cellId: string) => {
    console.log('🔓 Unmerging header cell:', { rowId, cellId });
    
    const newConfig = { ...tableConfig };
    
    // Handle main column header row
    if (rowId === '__main_columns__') {
      const column = newConfig.columns.find(c => c.id === cellId);
      if (!column) return;
      
      const colspan = column.headerColSpan || 1;
      const rowspan = column.headerRowSpan || 1;
      
      if (colspan === 1 && rowspan === 1) {
        toast.error(t('table.cells.notMerged'));
        return;
      }
      
      // Find the column index
      const columnIndex = newConfig.columns.findIndex(c => c.id === cellId);
      if (columnIndex === -1) return;
      
      // Restore all merged columns (those with headerColSpan=0 after this master)
      for (let i = columnIndex + 1; i < Math.min(columnIndex + colspan, newConfig.columns.length); i++) {
        const mergedCol = newConfig.columns[i];
        if (mergedCol.headerColSpan === 0) {
          mergedCol.headerColSpan = 1; // Restore
        }
      }
      
      // Reset master merge
      column.headerColSpan = 1;
      column.headerRowSpan = 1;
      
      console.log('✅ Main column header unmerged');
      pushToHistory(newConfig);
      toast.success(t('table.columns.unmerged'));
      return;
    }
    
    if (!newConfig.headerRows) return;
    
    const rowLevel = newConfig.headerRows.findIndex(r => r.id === rowId);
    if (rowLevel === -1) return;
    
    const row = newConfig.headerRows[rowLevel];
    const cellIndex = row.cells.findIndex(c => c.id === cellId);
    if (cellIndex === -1) return;
    
    const cell = row.cells[cellIndex];
    const colspan = cell.colspan || 1;
    const rowspan = cell.rowspan || 1;
    
    if (colspan === 1 && rowspan === 1) {
      toast.error(t('table.cells.notMerged'));
      return;
    }
    
    // Unmerge: split into individual cells (2D grid)
    // First, restore the master cell to normal (colspan=1, rowspan=1)
    cell.colspan = 1;
    cell.rowspan = 1;
    
    // Then, restore all cells that were marked with colspan=0
    if (rowspan > 1 || colspan > 1) {
      // For each affected row
      for (let r = rowLevel; r < rowLevel + rowspan && r < newConfig.headerRows.length; r++) {
        const affectedRow = newConfig.headerRows[r];
        
        // Find where to insert new cells (at the position of the master cell or after)
        let insertPosition = cellIndex;
        
        // For the master row, we need to insert cells after the master cell
        if (r === rowLevel && colspan > 1) {
          // Insert (colspan - 1) new cells after the master cell
          const newCells: HeaderCell[] = [];
          for (let c = 1; c < colspan; c++) {
            newCells.push({
              id: `header-cell-${Date.now()}-${r}-${c}`,
              label: '',
              colspan: 1,
              columnStartIndex: cell.columnStartIndex + c,
            });
          }
          affectedRow.cells.splice(insertPosition + 1, 0, ...newCells);
        }
        
        // For rows below the master row (vertical span)
        if (r > rowLevel) {
          // Restore cells that were marked with colspan=0
          // We need to insert colspan new cells
          const newCells: HeaderCell[] = [];
          for (let c = 0; c < colspan; c++) {
            newCells.push({
              id: `header-cell-${Date.now()}-${r}-${c}`,
              label: '',
              colspan: 1,
              columnStartIndex: cell.columnStartIndex + c,
            });
          }
          
          // Find insertion point by looking for cells with colspan=0
          let foundColspan0 = false;
          for (let i = 0; i < affectedRow.cells.length; i++) {
            if (affectedRow.cells[i].colspan === 0) {
              // Replace colspan=0 cells
              affectedRow.cells.splice(i, 1, newCells[0]);
              foundColspan0 = true;
              // Insert remaining cells
              for (let j = 1; j < newCells.length; j++) {
                affectedRow.cells.splice(i + j, affectedRow.cells[i + j]?.colspan === 0 ? 1 : 0, newCells[j]);
              }
              break;
            }
          }
          
          // If no colspan=0 found, insert at the calculated position
          if (!foundColspan0) {
            affectedRow.cells.splice(insertPosition, 0, ...newCells);
          }
        }
      }
    }
    
    console.log('✅ Header cell unmerged (2D)');
    pushToHistory(newConfig);
    toast.success(t('table.cells.splitHorizontal'));
  }, [tableConfig, pushToHistory, t]);

  // ===== DETECT HEADER GROUP CLICK =====
  const [selectedHeaderGroup, setSelectedHeaderGroup] = useState<string | null>(null);

  const handleHeaderGroupClick = useCallback((groupId: string) => {
    console.log('🔵 Header group clicked:', groupId);
    setSelectedHeaderGroup(groupId);
    setHeaderSelection(null); // Clear column selection
  }, []);

  // ===== MERGED COLUMN PROPERTIES =====
  const [showMergedPropertiesDialog, setShowMergedPropertiesDialog] = useState(false);
  const [selectedMergedColumn, setSelectedMergedColumn] = useState<number | null>(null);

  const handleUnmergeColumn = useCallback(() => {
    if (selectedMergedColumn === null) return;

    console.log('🔓 Unmerging column:', selectedMergedColumn);

    const newConfig = { ...tableConfig };
    const mergedColumn = newConfig.columns[selectedMergedColumn];

    if (!mergedColumn.isMerged || !mergedColumn.mergedFrom) {
      console.log('❌ Column is not merged!');
      return;
    }

    // Get the attribute keys that were merged
    const mergedAttributeKeys = mergedColumn.mergedFrom;

    console.log('📊 Unmerging attributes:', mergedAttributeKeys);

    // STEP 1: Remove split rows (rows created by old merge logic)
    const rowsToKeep = newConfig.rows.filter(row => {
      // Keep header rows
      if (row.isHeader) return true;
      
      // Remove split rows (keep only original rows)
      if (row.isSplitRow) {
        console.log('🗑️ Removing split row:', row.id);
        return false;
      }
      
      return true;
    });

    // Update row indices
    rowsToKeep.forEach((row, idx) => {
      row.index = idx;
    });

    newConfig.rows = rowsToKeep;

    // STEP 2: Re-create individual columns
    const newColumns: any[] = [];
    mergedAttributeKeys.forEach((attrKey, idx) => {
      const colWidth = mergedColumn.width / mergedAttributeKeys.length;
      newColumns.push({
        id: `col-${Date.now()}-${idx}`,
        index: selectedMergedColumn + idx,
        label: attrKey,
        attributeKey: attrKey,
        dataType: 'string' as const,
        width: colWidth,
      });
    });

    // Remove merged column and insert individual columns
    const remainingColumns = newConfig.columns.filter((_, idx) => idx !== selectedMergedColumn);
    remainingColumns.splice(selectedMergedColumn, 0, ...newColumns);

    // Update indices
    remainingColumns.forEach((col, idx) => {
      col.index = idx;
    });

    newConfig.columns = remainingColumns;

    // STEP 3: Update rows - split merged cells back into individual cells
    newConfig.rows = newConfig.rows.map(row => {
      if (row.isHeader) return row;

      const newData = { ...row.data };

      // Remove merged cell
      delete newData[mergedColumn.id];

      // Add individual cells
      newColumns.forEach(col => {
        const value = row.allAttributes?.[col.attributeKey] || '';
        newData[col.id] = {
          id: `${row.id}-${col.id}`,
          rowIndex: row.index,
          colIndex: col.index,
          value,
          dataType: 'string',
          isReadOnly: false,
          attributeRef: col.attributeKey,
        };
      });

      return { ...row, data: newData };
    });

    console.log('✅ Column unmerged successfully and split rows removed');
    pushToHistory(newConfig);
    setShowMergedPropertiesDialog(false);
    setSelectedMergedColumn(null);
  }, [selectedMergedColumn, tableConfig, pushToHistory]);

  // ===== REMOVED: MERGE CELLS =====
  // NOTE: Normal cells should NOT be mergeable to prevent data integrity issues.
  // Only header cells can be merged. This function and the corresponding UI have been removed.

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Ctrl+Z - Undo
      if ((e.ctrlKey || e.metaKey) && e.key === 'z' && !e.shiftKey) {
        e.preventDefault();
        handleUndo();
      }
      
      // Ctrl+Shift+Z or Ctrl+Y - Redo
      if (((e.ctrlKey || e.metaKey) && e.key === 'z' && e.shiftKey) ||
          ((e.ctrlKey || e.metaKey) && e.key === 'y')) {
        e.preventDefault();
        handleRedo();
      }
      
      // Ctrl+P - Print/Export
      if ((e.ctrlKey || e.metaKey) && e.key === 'p') {
        e.preventDefault();
        setShowPrintDialog(true);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleUndo, handleRedo]);

  // ===== SAVE/LOAD PROJECT =====
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  // ===== RELOAD VALUES FROM BLOCKS =====
  const handleReloadValues = useCallback(() => {
    console.log('🔄🔄🔄 MANUAL FULL-SYNC: Reloading ALL blocks/connections from BLOCK.SYSTEM...');
    console.log(`📊 INPUT: ${blocks.length} blocks, ${connections.length} connections`);
    console.log(`📋 CURRENT TABLE: ${tableConfig.rows.filter((r: any) => !r.isHeader).length} data rows`);
    
    const existingBlockIds = new Set(
      tableConfig.rows
        .filter((row: any) => !row.isHeader && row.blockId)
        .map((row: any) => row.blockId)
    );
    
    const existingConnectionIds = new Set(
      tableConfig.rows
        .filter((row: any) => !row.isHeader && row.connectionId)
        .map((row: any) => row.connectionId)
    );
    
    console.log('📊 EXISTING in table:', existingBlockIds.size, 'block rows,', existingConnectionIds.size, 'connection rows');
    
    // FIRST: Restore lengths from dimensions (recover true values!)
    console.log('🩹 Restoring length values from dimension elements...');
    const restoredBlocks = updateAllLengthAttributes(blocks);
    
    // Update blocks in parent
    if (onBlocksChange) {
      console.log('✅ Updating blocks with restored lengths');
      onBlocksChange(restoredBlocks);
    }
    
    const config = { ...tableConfig };
    
    // DESTRUCTIVE REBUILD: Instead of patching, we regenerate ALL data rows
    // This ensures complete synchronization with the current block state
    console.log('🔥 BURNING AND REBUILDING TABLE ROWS...');
    
    // 1. Keep only header rows
    const headerRows = config.rows.filter(r => r.isHeader);
    const oldRows = config.rows.filter(r => !r.isHeader);
    
    // 2. Generate rows for ALL blocks
    const blockRows = restoredBlocks.map((block, idx) => {
      // Try to preserve row ID if it existed before (to keep selection state if possible)
      const existingRow = oldRows.find(r => r.blockId === block.id);
      const rowId = existingRow ? existingRow.id : `row-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
      
      const allAttrs: any = { 'Name': block.blockData.name || 'Unnamed' };
      block.blockData.attributes?.forEach((attr: any) => {
        allAttrs[attr.name] = attr.value;
      });
      
      const cells: { [columnId: string]: TableCell } = {};
      config.columns.forEach((col: any, colIdx: number) => {
        let attributeRef = '';
        if (col.type === 'name') {
          attributeRef = 'Name';
        } else if (col.type === 'attribute' && col.sourceAttributeId) {
          attributeRef = col.sourceAttributeId;
        } else if (col.attributeKey) {
          attributeRef = col.attributeKey;
        }
        
        const value = attributeRef && allAttrs[attributeRef] !== undefined ? allAttrs[attributeRef] : '';
        
        cells[col.id] = {
          id: `cell-${rowId}-${col.id}`,
          rowIndex: headerRows.length + idx, // Will be corrected later
          colIndex: colIdx,
          value,
          dataType: col.dataType || 'text',
          style: {},
          attributeRef,
        };
      });
      
      return {
        id: rowId,
        index: 0, // Will be set later
        height: 30,
        isHeader: false,
        isEmpty: false,
        blockId: block.id,
        locked: false,
        data: cells,
        allAttributes: allAttrs,
      };
    });
    
    // 3. Generate rows for ALL connections
    const connectionRows = connections.map((connection, idx) => {
      const existingRow = oldRows.find(r => r.connectionId === connection.id);
      const rowId = existingRow ? existingRow.id : `row-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
      
      const allAttrs: any = { 'Name': 'Verbindung' };
      connection.attributes?.forEach((attr: any) => {
        allAttrs[attr.name] = attr.value;
      });
      
      const cells: { [columnId: string]: TableCell } = {};
      config.columns.forEach((col: any, colIdx: number) => {
        let attributeRef = '';
        if (col.type === 'name') {
          attributeRef = 'Name';
        } else if (col.type === 'attribute' && col.sourceAttributeId) {
          attributeRef = col.sourceAttributeId;
        } else if (col.attributeKey) {
          attributeRef = col.attributeKey;
        }
        
        const value = attributeRef && allAttrs[attributeRef] !== undefined ? allAttrs[attributeRef] : '';
        
        cells[col.id] = {
          id: `cell-${rowId}-${col.id}`,
          rowIndex: headerRows.length + blockRows.length + idx, // Will be corrected later
          colIndex: colIdx,
          value,
          dataType: col.dataType || 'text',
          style: {},
          attributeRef,
        };
      });
      
      return {
        id: rowId,
        index: 0, // Will be set later
        height: 30,
        isHeader: false,
        isEmpty: false,
        connectionId: connection.id,
        locked: false,
        data: cells,
        allAttributes: allAttrs,
      };
    });
    
    // 4. Combine and Re-Index
    config.rows = [...headerRows, ...blockRows, ...connectionRows];
    config.rows.forEach((row, idx) => {
      row.index = idx;
      // Also update cell rowIndexes
      Object.values(row.data).forEach((cell: any) => {
        cell.rowIndex = idx;
      });
    });
    
    const finalDataRows = blockRows.length + connectionRows.length;
    console.log(`📋 RESULT: Rebuilt table with ${finalDataRows} data rows`);
    console.log('✅ Full table rebuild complete!');
    
    toast.success(t('table.status.rebuilt').replace('{count}', finalDataRows.toString()));
    
    // Push to history so Ctrl+Z works
    pushToHistory(config);
  }, [blocks, connections, onBlocksChange, tableConfig, pushToHistory, t]);
  
  const handleSave = useCallback(async () => {
    // Helper to generate project data
    const getProjectData = (filename: string) => {
      const now = new Date().toISOString();
      const serializableTableConfig = {
        ...tableConfig,
        groupConfig: tableConfig.groupConfig ? {
          ...tableConfig.groupConfig,
          collapsedGroups: Array.from(tableConfig.groupConfig.collapsedGroups),
        } : undefined,
      };

      const project: IsomProject = {
        name: filename,
        version: '2.1',
        created: now,
        modified: now,
        blocks,
        connections,
        systems: [],
        tableConfig: serializableTableConfig,
      };

      return new Blob([JSON.stringify(project, null, 2)], {
        type: 'application/json',
      });
    };

    if ('showSaveFilePicker' in window) {
      try {
        const handle = await (window as any).showSaveFilePicker({
          suggestedName: `${projectData.name}.isomproject`,
          types: [{
            description: 'IsoCAD Projekt',
            accept: { 'application/json': ['.isomproject'] }
          }],
        });
        
        const name = handle.name.replace(/\.isomproject$/i, '');
        if (onUpdateProjectData) onUpdateProjectData({ name });
        
        const blob = getProjectData(name);
        const writable = await handle.createWritable();
        await writable.write(blob);
        await writable.close();
        
        toast.success(t('editor.status.saved'));
      } catch (err: any) {
        if (err.name !== 'AbortError') {
          console.error(err);
          toast.error('Fehler beim Speichern');
        }
      }
    } else {
      const blob = getProjectData(projectData.name);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${projectData.name.replace(/\s+/g, '_')}.isomproject`;
      a.click();
      URL.revokeObjectURL(url);
      
      toast.success(t('editor.status.saved'));
    }
  }, [projectData.name, blocks, connections, tableConfig, onUpdateProjectData]);

  const handleLoad = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleFileChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const project: IsomProject = JSON.parse(event.target?.result as string);

        // Update blocks and connections
        if (onBlocksChange && project.blocks) {
          onBlocksChange(project.blocks);
        }
        if (onConnectionsChange && project.connections) {
          onConnectionsChange(project.connections);
        }

        // Update table config
        if (project.tableConfig) {
          // Deserialize groupConfig.collapsedGroups (Array -> Set)
          const deserializedTableConfig = {
            ...project.tableConfig,
            groupConfig: project.tableConfig.groupConfig ? {
              ...project.tableConfig.groupConfig,
              collapsedGroups: new Set(
                Array.isArray(project.tableConfig.groupConfig.collapsedGroups) 
                  ? project.tableConfig.groupConfig.collapsedGroups 
                  : []
              ),
            } : createDefaultGroupConfig(),
          };
          
          setTableConfig(deserializedTableConfig);
          
          // Save to localStorage (with serialized Set)
          const serializableTableConfig = {
            ...deserializedTableConfig,
            groupConfig: deserializedTableConfig.groupConfig ? {
              ...deserializedTableConfig.groupConfig,
              collapsedGroups: Array.from(deserializedTableConfig.groupConfig.collapsedGroups),
            } : undefined,
          };
          localStorage.setItem('cad_tableConfig', JSON.stringify(serializableTableConfig));
        }

        // Update project name
        if (onUpdateProjectData && project.name) {
          onUpdateProjectData({ name: project.name });
        }

        // Restore print configuration and presets
        if (project.lastPrintConfig) {
          try {
            savePrintConfig(project.lastPrintConfig);
            console.log('✅ Restored print configuration from project');
          } catch (error) {
            console.error('⚠️ Failed to restore print config:', error);
          }
        }
        
        if (project.printPresets && project.printPresets.length > 0) {
          try {
            // Convert date strings back to Date objects
            const restoredPresets = project.printPresets.map(preset => ({
              ...preset,
              createdAt: new Date(preset.createdAt),
              updatedAt: preset.updatedAt ? new Date(preset.updatedAt) : undefined,
            }));
            saveCustomPresets(restoredPresets);
            console.log(`✅ Restored ${restoredPresets.length} custom print preset(s)`);
          } catch (error) {
            console.error('⚠️ Failed to restore print presets:', error);
          }
        }
        
        const details = [];
        if (project.tableConfig) details.push('Tabellenkonfiguration');
        if (project.lastPrintConfig) details.push('Druckkonfiguration');
        if (project.printPresets && project.printPresets.length > 0) {
          details.push(`${project.printPresets.length} Druck-Preset${project.printPresets.length === 1 ? '' : 's'}`);
        }
        
        const detailsStr = details.length > 0 ? ` (${details.join(', ')})` : '';
        toast.success(`Projekt geladen${detailsStr}`);
      } catch (error) {
        console.error('Error loading project:', error);
        toast.error('Fehler beim Laden des Projekts');
      }
    };

    reader.readAsText(file);

    // Reset input so the same file can be loaded again
    e.target.value = '';
  }, [onBlocksChange, onConnectionsChange, onUpdateProjectData]);

  // ===== APPLY GROUPING =====
  // Compute grouped rows when grouping is enabled
  const displayedTableConfig = React.useMemo(() => {
    if (!tableConfig.groupConfig || !tableConfig.groupConfig.enabled) {
      return tableConfig;
    }
    
    // Build grouped rows
    const groupedRows = buildGroupedRows(
      tableConfig.rows,
      tableConfig.groupConfig,
      tableConfig.columns
    );
    
    return {
      ...tableConfig,
      rows: groupedRows,
    };
  }, [tableConfig]);

  return (
    <div 
      ref={containerRef}
      className="flex flex-col h-full overflow-hidden"
      style={{
        backgroundColor: 'var(--md-surface)',
        color: 'var(--md-on-surface)',
      }}
    >
      {/* Top Toolbar mit Hamburger-Menü */}
      <div
        className="flex items-center gap-2 px-4 py-2 border-b"
        style={{
          backgroundColor: 'var(--md-surface-container-low)',
          borderColor: 'var(--md-outline-variant)',
        }}
      >
        {/* Hamburger-Menü */}
        <Button 
          variant="ghost" 
          size="default" 
          onClick={() => setDrawerOpen(true)} 
          title={t('editor.actions.openMenu')}
        >
          <Menu className="w-5 h-5" />
        </Button>

        <div className="h-6 w-px bg-gray-300 mx-1" />

        {/* Undo/Redo */}
        <Button 
          variant="ghost" 
          size="default" 
          onClick={handleUndo} 
          disabled={history.past.length === 0}
          title="Rückgängig (Strg+Z)"
        >
          <Undo2 className="w-5 h-5" />
        </Button>

        <Button 
          variant="ghost" 
          size="default" 
          onClick={handleRedo} 
          disabled={history.future.length === 0}
          title="Wiederholen (Strg+Y)"
        >
          <Redo2 className="w-5 h-5" />
        </Button>

        <div className="h-6 w-px bg-gray-300 mx-1" />

        {/* Seitenleiste Ein/Aus */}
        <Button 
          variant="ghost" 
          size="default" 
          onClick={() => setShowSidebar(!showSidebar)} 
          title={showSidebar ? "Seitenleiste ausblenden" : "Seitenleiste anzeigen"}
        >
          <PanelLeft className="w-5 h-5" />
        </Button>

        <div className="h-6 w-px bg-gray-300 mx-1" />

        {/* Werte neu laden */}
        <Button
          variant="ghost"
          size="default"
          onClick={handleReloadValues}
          title={t('table.toolbar.reloadValues')}
        >
          <RefreshCw className="w-5 h-5" />
        </Button>

        <div className="h-6 w-px bg-gray-300 mx-1" />

        {/* Drucken */}
        <Button
          variant="ghost"
          size="default"
          onClick={() => setShowPrintDialog(true)}
          title={t('table.toolbar.print')}
        >
          <Printer className="w-5 h-5" />
        </Button>

        <div className="flex-1" />

        {/* Tutorial Help Button */}
        <TutorialHelpButton currentModule="system-table" variant="icon" />

        {/* Projektname */}
        <div className="text-sm font-medium">{projectData.name}</div>
      </div>

      {/* Navigation Drawer */}
      <NavigationDrawer
        isOpen={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        onNewFile={() => {
          if (confirm(t('common.confirmNewFile'))) {
            // Tabellen-Config zurücksetzen
            setTableConfig(createEmptyTableConfig());
            setHistory({ past: [], present: createEmptyTableConfig(), future: [] });
          }
        }}
        onSave={handleSave}
        onSaveAs={handleSave} // SystemTable hat keine separate SaveAs-Funktion
        onLoad={handleLoad}
        onExport={() => {
          // Export-Funktionalität später hinzufügen
          toast.info('Export-Funktionalität wird hinzugefügt');
        }}
        onImport={() => {
          // Import-Funktionalität später hinzufügen
          toast.info('Import-Funktionalität wird hinzugefügt');
        }}
        onOpenLibrary={() => {
          toast.info('Bibliothek wird hinzugefügt');
        }}
        onOpenHelp={() => {
          toast.info('Hilfe wird hinzugefügt');
        }}
        onOpenSettings={() => {
          toast.info('Einstellungen werden hinzugefügt');
        }}
      />

      <div className="flex flex-1 overflow-hidden relative">
        {/* Responsive Sidebar Container */}
        <div 
          className={cn(
            "h-full z-20 transition-all duration-300 ease-in-out flex flex-col border-r",
            // Mobile: Absolute overlay
            "absolute left-0 top-0 bottom-0 shadow-xl lg:shadow-none",
            showSidebar ? "translate-x-0" : "-translate-x-full",
            // Desktop: Relative sliding drawer
            "lg:static lg:translate-x-0",
            showSidebar ? "lg:w-80" : "lg:w-0 lg:border-none lg:overflow-hidden"
          )}
          style={{
            backgroundColor: 'var(--md-surface-container-low)',
            borderColor: 'var(--md-outline)'
          }}
        >
          <div className="w-80 h-full overflow-hidden flex flex-col">
            <TableSidebar
              tableConfig={tableConfig}
              blocks={blocks}
              connections={connections}
              onAddAttributeColumn={handleAttributeDrop}
              onCreateAttribute={handleCreateAttribute}
              onEditAttribute={handleEditAttribute}
              onDeleteAttribute={handleDeleteAttribute}
            />
          </div>
        </div>

        <div className="flex flex-col flex-1 overflow-hidden">
          {/* NEW: Main Toolbar - always visible */}
          <TableMainToolbar
            tableConfig={tableConfig}
            headerCellSelection={headerCellSelection}
            onMergeHeaderCells={handleMergeHeaderCells}
            onSplitHeaderCells={handleSplitHeaderCells}
            onClearSelection={() => setHeaderCellSelection([])}
            onAddColumn={handleAddColumn}
            onAddHeaderRow={handleAddHeaderRow}
            onResetHeaderRows={handleResetHeaderRows}
            onApplyBorderPreset={handleApplyBorderPreset}
            onOpenBorderDialog={handleOpenBorderDialog}
            onOpenPrintDialog={() => setShowPrintDialog(true)}
            onOpenGroupByPanel={() => setShowGroupByPanel(true)}
            onToggleTotalsRow={handleToggleTotalsRow}
            onToggleSidebar={() => setShowSidebar(!showSidebar)}
            isSidebarVisible={showSidebar}
          />

          {/* Table Area with integrated Add Column */}
          <div className="flex-1 overflow-auto">
            <TableGrid
            tableConfig={displayedTableConfig}
            selection={selection}
            editingCell={editingCell}
            onSelectionChange={setSelection}
            onCellEdit={setEditingCell}
            onCellValueChange={handleCellValueChange}
            onDeleteRow={handleDeleteRow}
            onAddColumn={handleAddColumn}
            onDeleteColumn={handleDeleteColumn}
            onAttributeDrop={handleAttributeDrop}
            onColumnResize={handleColumnResize}
            onColumnReorder={handleColumnReorder}
            onHeaderSelectionChange={setHeaderSelection}
            onHeaderDoubleClick={(colIdx) => {
              setHeaderSelection([colIdx]);
              setShowRenameDialog(true);
            }}
            onHeaderGroupClick={handleHeaderGroupClick}
            onMergedColumnDoubleClick={(colIdx) => {
              setSelectedMergedColumn(colIdx);
              setShowMergedPropertiesDialog(true);
            }}
            onAddHeaderRow={handleAddHeaderRow}
            onInsertHeaderRowAbove={handleInsertHeaderRowAbove}
            onDeleteHeaderRow={handleDeleteHeaderRow}
            onResetHeaderRows={handleResetHeaderRows}
            headerCellSelection={headerCellSelection}
            onHeaderCellClick={handleHeaderCellClick}
            editingHeaderCell={editingHeaderCell}
            onHeaderCellEdit={setEditingHeaderCell}
            onHeaderCellValueChange={handleHeaderCellValueChange}
            onToggleGroupCollapse={handleToggleGroupCollapse}
            onUpdateTotalsAggregation={handleUpdateTotalsAggregation}
          />
          </div>
        </div>
        
        {/* Group By Panel - Right Sidebar */}
        {showGroupByPanel && tableConfig.groupConfig && (
          <GroupByPanel
            groupConfig={tableConfig.groupConfig}
            columns={tableConfig.columns}
            onUpdateGroupConfig={handleUpdateGroupConfig}
            onClose={() => setShowGroupByPanel(false)}
          />
        )}
      </div>

      {/* Floating toolbar for header selection */}
      {headerSelection && headerSelection.length > 0 && (
        <div
          className="fixed bottom-8 left-1/2 transform -translate-x-1/2 flex gap-2 px-4 py-3 rounded-lg shadow-xl z-50"
          style={{
            backgroundColor: 'var(--md-surface-container-high)',
            border: '1px solid var(--md-outline)',
          }}
        >
          <div className="flex items-center gap-2 pr-3 border-r" style={{ borderColor: 'var(--md-outline-variant)' }}>
            <span className="text-sm font-medium" style={{ color: 'var(--md-on-surface)' }}>
              {headerSelection.length} Spalte{headerSelection.length > 1 ? 'n' : ''} ausgewählt
            </span>
          </div>
          
          {/* Zusammenführen button - REMOVED */}
          
          {/* Überschrift button - REMOVED */}
          
          {headerSelection.length === 1 && (
            <button
              onClick={() => setShowRenameDialog(true)}
              className="px-4 py-2 rounded font-medium transition-colors"
              style={{
                backgroundColor: 'var(--md-tertiary)',
                color: 'var(--md-on-tertiary)',
              }}
            >
              Umbenennen
            </button>
          )}
          
          <button
            onClick={() => setHeaderSelection(null)}
            className="px-3 py-2 rounded"
            style={{
              backgroundColor: 'var(--md-surface-variant)',
              color: 'var(--md-on-surface-variant)',
            }}
          >
            Abbrechen
          </button>
        </div>
      )}

      {/* Merge dialog */}
      {showMergeDialog && headerSelection && (
        <MergeColumnsDialog
          columnNames={headerSelection.map(idx => tableConfig.columns[idx]?.label || '')}
          onConfirm={handleMergeColumns}
          onCancel={() => setShowMergeDialog(false)}
        />
      )}

      {/* Rename dialog */}
      {showRenameDialog && headerSelection && (
        <RenameColumnDialog
          columnName={tableConfig.columns[headerSelection[0]]?.label || ''}
          onConfirm={handleRenameColumn}
          onCancel={() => setShowRenameDialog(false)}
        />
      )}

      {/* Add Header Row dialog - REMOVED */}

      {/* REMOVED: Floating toolbar for normal cell selection 
          Normal cells can be selected but NOT merged.
          Only header cells can be merged to prevent data integrity issues. */}

      {/* Floating toolbar for header group selection - DELETE GROUP */}
      {selectedHeaderGroup && (
        <div
          className="fixed bottom-8 left-1/2 transform -translate-x-1/2 flex gap-2 px-4 py-3 rounded-lg shadow-xl z-50"
          style={{
            backgroundColor: 'var(--md-surface-container-high)',
            border: '1px solid var(--md-outline)',
          }}
        >
          <div className="flex items-center gap-2 pr-3 border-r" style={{ borderColor: 'var(--md-outline-variant)' }}>
            <span className="text-sm font-medium" style={{ color: 'var(--md-on-surface)' }}>
              Überschrift ausgewählt: {tableConfig.headerGroups?.find(g => g.id === selectedHeaderGroup)?.label || ''}
            </span>
          </div>
          
          <button
            onClick={() => {
              handleDeleteHeaderGroup(selectedHeaderGroup);
              setSelectedHeaderGroup(null);
            }}
            className="px-4 py-2 rounded font-medium transition-colors"
            style={{
              backgroundColor: '#d32f2f',
              color: '#ffffff',
            }}
          >
            Überschrift löschen
          </button>
          
          <button
            onClick={() => setSelectedHeaderGroup(null)}
            className="px-3 py-2 rounded"
            style={{
              backgroundColor: 'var(--md-surface-variant)',
              color: 'var(--md-on-surface-variant)',
            }}
          >
            Abbrechen
          </button>
        </div>
      )}

      {/* Hidden file input for loading */}
      <input
        ref={fileInputRef}
        type="file"
        accept=".isomproject"
        className="hidden"
        onChange={handleFileChange}
      />

      {/* Merged column properties dialog */}
      {showMergedPropertiesDialog && selectedMergedColumn !== null && (
        <MergedColumnDialog
          columnName={tableConfig.columns[selectedMergedColumn]?.label || ''}
          mergedAttributes={tableConfig.columns[selectedMergedColumn]?.mergedFrom || []}
          onUnmerge={handleUnmergeColumn}
          onCancel={() => {
            setShowMergedPropertiesDialog(false);
            setSelectedMergedColumn(null);
          }}
        />
      )}
      
      {/* Border areas dialog */}
      {showBorderDialog && (
        <BorderAreasDialog
          onApply={handleApplyBorderAreas}
          onCancel={() => setShowBorderDialog(false)}
        />
      )}
      
      {/* Add Column dialog - creates new attribute and adds it as column */}
      {showAddColumnDialog && (
        <AddAttributeDialog
          open={showAddColumnDialog}
          blocks={blocks}
          connections={connections}
          onConfirm={(config) => {
            handleCreateAttribute(config);
            setShowAddColumnDialog(false);
          }}
          onClose={() => setShowAddColumnDialog(false)}
        />
      )}
      
      {/* Print/Export Dialog */}
      <PrintDialog
        isOpen={showPrintDialog}
        onClose={() => setShowPrintDialog(false)}
        tableConfig={tableConfig}
        projectName={projectData.name}
        projectData={{
          customAttributes: projectData.customAttributes,
          createdAt: undefined,
          modifiedAt: undefined,
        }}
        author={undefined}
      />
    </div>
  );
}